import {
  calculateBorderLength,
  drawStoryline,
} from '../../../src/js/utils/drawerB'

import iStoryline from '../../../src/js'
import * as d3Fetch from 'd3-fetch'
import Snap from 'snapsvg'
import $ from 'jquery'
import {
  createTimeoutSignal,
  getConfiguredLLMEndpoint,
  getStoryInsightEndpoint,
} from './ai/endpoints'
import { requestAIStoryInsightCandidates } from './ai/story-insight-client'
import { requestStorySummary } from './ai/summary-client'
import {
  createAnnotationExport as buildAnnotationExport,
  normalizeImportedAuthoringAnnotations,
} from './authoring/import-export'
import {
  createAuthoringLayer,
  ensureAuthoringLayerState,
  getActiveAuthoringLayer as selectActiveAuthoringLayer,
  getVisibleSavedHighlightBands as selectVisibleSavedHighlightBands,
  getVisibleSavedStorySnippets as selectVisibleSavedStorySnippets,
} from './authoring/layers'
import {
  downloadAnnotationJson,
  readStoredAuthoringAnnotations,
  writeStoredAuthoringAnnotations,
} from './authoring/persistence'
import {
  bindAIInsightPanelControls,
  isAIInsightPanelVisible as isAIInsightPanelVisibleDom,
  setAIInsightPanelVisible as setAIInsightPanelVisibleDom,
  setAIInsightStatus as setAIInsightPanelStatus,
} from './ui/ai-insight-panel'
import { bindAuthoringPanelControls } from './ui/authoring-panel'
import {
  getCheckedEventTypeFilters,
  initEventFilterActionButtons,
} from './ui/filters'
import { initOnboardingGuide } from './ui/onboarding'
import { initStoryModal } from './ui/story-modal'
import { initZoomPanControls } from './ui/zoom-pan'
import {
  getBandFocusBounds as getBandFocusBoundsFromRender,
  getBandStartFocusBounds as getBandStartFocusBoundsFromRender,
  renderActiveInsightBand as renderActiveInsightBandLayer,
  renderHighlightBands as renderHighlightBandsLayer,
  resetAuthorBandEmphasis as resetAuthorBandEmphasisLayer,
  showAuthorBandEmphasis as showAuthorBandEmphasisLayer,
} from './render/author-bands'
import {
  getEventRendererKind,
  isChoiceOrDeathEvent,
} from './render/events/event-dispatch'
import {
  collectFamilyInteractionLineGroups,
  drawFamilyInteractionLines,
} from './render/events/family-interaction-events'
import { renderMovingInEvents } from './render/events/moving-in-events'
import { renderStandardInteractionEvent } from './render/events/standard-events'
import { ensureSvgGlowFilter } from './render/events/tooltips'
import { showVideoEventModal } from './render/events/video-modal'
import { renderSelectionMarks as renderSelectionMarksLayer } from './render/selection-marks'
import {
  AI_INSIGHT_MAX_BAND_ORDER_SPAN as aiInsightMaxBandOrderSpan,
  AI_INSIGHT_MAX_BAND_TIME_SPAN as aiInsightMaxBandTimeSpan,
  ACTIVE_DATASET,
  AUTHORING_LEGACY_STORAGE_KEYS as authoringLegacyStorageKeys,
  AUTHOR_BAND_LANE_SPACING as authorBandLaneSpacing,
  AUTHOR_BAND_MIN_RIBBON_Y as authorBandMinRibbonY,
  AUTHOR_BAND_STORYLINE_GAP as authorBandStorylineGap,
  AUTHORING_STORAGE_KEY as authoringStorageKey,
  DEFAULT_AUTHORING_LAYER_ID,
  EVENT_DATA_PATH,
  EVENT_ICON_GENERAL_TYPES,
  EVENT_ICON_PNG_TYPES,
  PARTICIPANTS_PATH,
  PLAYER_COLORS,
  STORY_SCRIPT_FILE,
  SVG_HEIGHT,
  SVG_INITIAL_VIEW_BOX,
  SVG_WIDTH,
  VIDEO_EVENT_TYPES,
  VIDEO_PATH,
} from './config'
import {
  getEventCharacters,
  normalizeEventRecords,
  normalizeEvidenceIds,
} from './data/event-normalization'
import {
  getFallbackBandColor,
  isAccessibleBandColor,
  normalizeHexColor,
} from './utils/color'
import {
  cleanDraftText,
  escapeHtml,
  summarizeEditedParagraph,
} from './utils/text'
import { formatEventTime, getVideoClipRangeFromEvent } from './utils/time'

// This entry file is the orchestration layer for the Sims 4 prototype. Rendering,
// UI widgets, authoring persistence, and AI request helpers are being split into
// modules, while this file still wires those modules to the active SVG instance.

// -----------------------------------------------------------------------------
// Runtime state
// -----------------------------------------------------------------------------

let allEventData = [] // Store all event data globally for filtering
const selectedEventIds = new Set()
const eventById = new Map()
const eventVisualsById = new Map()
const eventElementsById = new Map()
const bandRibbonYById = new Map()
let authoringSelectionMode = false
let savedHighlightBands = []
let savedStorySnippets = []
let auditLog = []
let authoringLayers = []
let activeAuthoringLayerId = DEFAULT_AUTHORING_LAYER_ID
let aiInsightCandidates = []
let activeInsightCandidateId = null
let currentStoryDraft = null
let lastSummaryModel = ''
let currentGraph = null
let currentStorylines = []
let eventDrawVersion = 0
let activeEventTypeFilterTypes = null
let editingSavedSnippetId = null

// -----------------------------------------------------------------------------
// Event and AI response normalization
// -----------------------------------------------------------------------------

function normalizeEventData(events = []) {
  const normalizedEvents = normalizeEventRecords(events)
  normalizedEvents.forEach(event => {
    eventById.set(event._eventId, event)
  })
  return normalizedEvents
}

function clampConfidence(value) {
  const number = Number(value)
  if (!Number.isFinite(number)) return 0.5
  return Math.max(0, Math.min(1, number))
}

// AI-suggested colors are accepted only when they remain readable next to the
// character-line palette; otherwise use a deterministic fallback.
function resolveBandColor(rawColor, rawRationale, label, warnings = []) {
  const normalized = normalizeHexColor(rawColor)
  const characterColors =
    typeof playerColour === 'undefined' ? [] : Object.values(playerColour)
  if (normalized && isAccessibleBandColor(normalized, characterColors)) {
    return {
      color: normalized,
      colorSource: 'ai',
      colorRationale:
        cleanDraftText(rawRationale, 180) ||
        'AI suggested a high-contrast color for this band.',
    }
  }
  if (normalized) {
    warnings.push(
      'Suggested color failed accessibility checks and was replaced.'
    )
  } else {
    warnings.push('Suggested color was missing and was replaced.')
  }
  return {
    color: getFallbackBandColor(label),
    colorSource: normalized ? 'fallback-from-ai' : 'fallback',
    colorRationale:
      'Fallback color selected for contrast and character-line separation.',
  }
}

// Guard the editable story draft shape before the authoring panel uses it.
function validateStoryDraft(rawDraft, events) {
  if (!rawDraft || typeof rawDraft !== 'object') {
    throw new Error('LLM response was not a JSON object')
  }

  const warnings = []
  const label = cleanDraftText(rawDraft.label, 64)
  const paragraph = cleanDraftText(rawDraft.paragraph, 1400)
  if (!label) throw new Error('LLM response is missing a label')
  if (!paragraph) throw new Error('LLM response is missing a paragraph')

  const oneSentenceSummary =
    cleanDraftText(rawDraft.oneSentenceSummary, 220) ||
    summarizeEditedParagraph(paragraph)
  const alternativeLabels = Array.isArray(rawDraft.alternativeLabels)
    ? rawDraft.alternativeLabels
        .map(item => cleanDraftText(item, 48))
        .filter(Boolean)
        .slice(0, 5)
    : []
  const evidenceEventIds = normalizeEvidenceIds(
    rawDraft.evidenceEventIds,
    events,
    warnings
  )
  const colorChoice = resolveBandColor(
    rawDraft.color,
    rawDraft.colorRationale,
    label,
    warnings
  )

  return {
    label,
    oneSentenceSummary,
    paragraph,
    alternativeLabels,
    evidenceEventIds,
    confidence: clampConfidence(rawDraft.confidence),
    color: colorChoice.color,
    colorSource: colorChoice.colorSource,
    colorRationale: colorChoice.colorRationale,
    model: cleanDraftText(rawDraft.model, 80) || 'external',
    validationWarnings: warnings,
  }
}

// -----------------------------------------------------------------------------
// Authoring persistence and layer selectors
// -----------------------------------------------------------------------------

// Snapshot helpers let import/export modules work with one object instead of
// reaching into each global variable directly.
function getAuthoringStateSnapshot() {
  return {
    highlightBands: savedHighlightBands,
    storySnippets: savedStorySnippets,
    auditLog,
    layers: authoringLayers,
    activeLayerId: activeAuthoringLayerId,
  }
}

function applyAuthoringStateSnapshot(state) {
  savedHighlightBands = Array.isArray(state.highlightBands)
    ? state.highlightBands
    : []
  savedStorySnippets = Array.isArray(state.storySnippets)
    ? state.storySnippets
    : []
  auditLog = Array.isArray(state.auditLog) ? state.auditLog : []
  authoringLayers = Array.isArray(state.layers) ? state.layers : []
  activeAuthoringLayerId = state.activeLayerId || activeAuthoringLayerId
}

function ensureAuthoringLayers() {
  applyAuthoringStateSnapshot(
    ensureAuthoringLayerState(getAuthoringStateSnapshot())
  )
}

function getActiveAuthoringLayer() {
  ensureAuthoringLayers()
  return selectActiveAuthoringLayer(getAuthoringStateSnapshot())
}

function getVisibleSavedHighlightBands() {
  ensureAuthoringLayers()
  return selectVisibleSavedHighlightBands(getAuthoringStateSnapshot())
}

function getVisibleSavedStorySnippets() {
  ensureAuthoringLayers()
  return selectVisibleSavedStorySnippets(getAuthoringStateSnapshot())
}

// Load from the active dataset storage key, with legacy-key fallback handled by
// the persistence helper for older prototype annotations.
function loadAuthoringAnnotations() {
  try {
    const data = readStoredAuthoringAnnotations(
      window.localStorage,
      authoringStorageKey,
      authoringLegacyStorageKeys
    )
    if (!data) {
      ensureAuthoringLayers()
      return
    }
    applyAuthoringStateSnapshot({
      highlightBands: data.highlightBands || [],
      storySnippets: data.storySnippets || [],
      auditLog: data.auditLog || [],
      layers: Array.isArray(data.layers) ? data.layers : [],
      activeLayerId: data.activeLayerId || activeAuthoringLayerId,
    })
    ensureAuthoringLayers()
    lastSummaryModel = data.llmProvenance ? data.llmProvenance.model || '' : ''
  } catch (error) {
    console.warn('Could not load authoring annotations:', error)
  }
}

// Persist the same schema that the Download JSON action exports.
function persistAuthoringAnnotations() {
  const annotations = createAnnotationExport()
  writeStoredAuthoringAnnotations(
    window.localStorage,
    authoringStorageKey,
    annotations
  )
}

// Attach current model provenance while keeping schema assembly out of the UI.
function createAnnotationExport() {
  ensureAuthoringLayers()
  return buildAnnotationExport(getAuthoringStateSnapshot(), {
    currentStoryDraft,
    lastSummaryModel,
  })
}

// -----------------------------------------------------------------------------
// Event selection registry
// -----------------------------------------------------------------------------

function getSelectedEvents() {
  return Array.from(selectedEventIds)
    .map(id => eventById.get(id))
    .filter(Boolean)
    .sort((a, b) => a.timestamp - b.timestamp)
}

// Event icons are redrawn after filtering, so cached SVG elements and visual
// positions must be rebuilt each time.
function clearEventVisualRegistry() {
  resetAuthorBandEmphasis()
  eventVisualsById.clear()
  eventElementsById.clear()
  renderSelectionMarks()
}

function getIdentitySvgMatrix() {
  return { a: 1, b: 0, c: 0, d: 1, e: 0, f: 0 }
}

// Store transforms as plain matrices so hover emphasis can enlarge original SVG
// icons without swapping in placeholder marks.
function getElementTransformMatrix(node) {
  if (!node || !node.transform || !node.transform.baseVal) {
    return getIdentitySvgMatrix()
  }
  const consolidated = node.transform.baseVal.consolidate()
  if (!consolidated) return getIdentitySvgMatrix()
  const matrix = consolidated.matrix
  return {
    a: matrix.a,
    b: matrix.b,
    c: matrix.c,
    d: matrix.d,
    e: matrix.e,
    f: matrix.f,
  }
}

function toSvgMatrix(matrix) {
  return `matrix(${[matrix.a, matrix.b, matrix.c, matrix.d, matrix.e, matrix.f]
    .map(value => Number(value.toFixed(3)))
    .join(' ')})`
}

function createSvgMatrix(matrix) {
  const svgMatrix = mySvg.createSVGMatrix()
  svgMatrix.a = matrix.a
  svgMatrix.b = matrix.b
  svgMatrix.c = matrix.c
  svgMatrix.d = matrix.d
  svgMatrix.e = matrix.e
  svgMatrix.f = matrix.f
  return svgMatrix
}

function findEventIdFromTarget(target) {
  let node = target
  while (node && node !== mySvg) {
    if (node.getAttribute && node.getAttribute('data-event-id')) {
      return node.getAttribute('data-event-id')
    }
    node = node.parentNode
  }
  return null
}

// Event renderers call this once per visible icon/shape to make authoring
// selection independent from the specific event-rendering branch.
function registerSelectableEventElement(element, event, visual) {
  if (!element || !event || !event._eventId) return
  const node = element.node || element
  if (!node || !node.setAttribute) return
  node.setAttribute('data-event-id', event._eventId)
  node.setAttribute('data-event-type', event.eventType || '')
  node.style.cursor = authoringSelectionMode ? 'copy' : 'pointer'
  if (!eventVisualsById.has(event._eventId)) {
    eventVisualsById.set(event._eventId, [])
  }
  if (visual) {
    eventVisualsById.get(event._eventId).push(visual)
    if (!eventElementsById.has(event._eventId)) {
      eventElementsById.set(event._eventId, [])
    }
    eventElementsById.get(event._eventId).push({
      element,
      visual,
      originalTransform: node.getAttribute('transform') || '',
      originalMatrix: getElementTransformMatrix(node),
    })
  }
  renderSelectionMarks()
  renderHighlightBands()
}

// Capture clicks at the SVG level so each renderer does not need its own
// authoring-mode click handler.
function handleAuthoringSvgClick(event) {
  if (!authoringSelectionMode) return
  const eventId = findEventIdFromTarget(event.target)
  if (!eventId) return
  event.preventDefault()
  event.stopPropagation()
  toggleEventSelection(eventId)
}

function toggleEventSelection(eventId) {
  if (selectedEventIds.has(eventId)) {
    selectedEventIds.delete(eventId)
  } else {
    selectedEventIds.add(eventId)
  }
  currentStoryDraft = null
  updateAuthoringPanel()
  renderSelectionMarks()
}

function clearEventSelection() {
  selectedEventIds.clear()
  currentStoryDraft = null
  updateAuthoringPanel()
  renderSelectionMarks()
}

function renderSelectionMarks() {
  renderSelectionMarksLayer({
    svg,
    selectedEventIds,
    eventVisualsById,
  })
}

// Bounds are used by saved snippets and AI candidates to place highlight bands
// and to navigate the viewport to the related story region.
function getSelectionBounds(events) {
  const points = events.flatMap(event => getEventVisualPoints(event._eventId))
  if (!points.length) return null
  const xs = points.map(point => point.x)
  const ys = points.map(point => point.y)
  return {
    x: Math.min(...xs) - 60,
    y: Math.max(20, Math.min(...ys) - 110),
    width: Math.max(120, Math.max(...xs) - Math.min(...xs) + 120),
    height: Math.max(170, Math.max(...ys) - Math.min(...ys) + 190),
  }
}

// Fall back to graph coordinates when a saved snippet references events that
// are not currently represented by a cached SVG element.
function getEventVisualPoints(eventId) {
  const visuals = eventVisualsById.get(eventId) || []
  if (visuals.length) return visuals
  const event = eventById.get(eventId)
  if (!event || !currentGraph) return []
  const ids = Array.isArray(event.interacteeID)
    ? [event.interactorID, ...event.interacteeID]
    : [event.interactorID]
  return ids
    .filter(id => id !== undefined && id !== null)
    .map(id => {
      const playerId = `Player${id}`
      return {
        x: currentGraph.getCharacterX(playerId, event.timestamp),
        y: currentGraph.getCharacterY(playerId, event.timestamp),
        radius: 18,
      }
    })
    .filter(point => point.x >= 0 && point.y >= 0)
}

function getStorylinePoint(point) {
  if (Array.isArray(point)) {
    return { x: Number(point[0]), y: Number(point[1]) }
  }
  return { x: Number(point.x), y: Number(point.y) }
}

// -----------------------------------------------------------------------------
// Author band rendering and viewport navigation
// -----------------------------------------------------------------------------

// Render modules receive a focused context object instead of importing globals.
function getAuthorBandRenderContext() {
  return {
    svg,
    mySvg,
    documentRef: document,
    viewBox,
    bandRibbonYById,
    eventById,
    eventElementsById,
    currentStorylines,
    currentGraph,
    playerColour,
    authorBandMinRibbonY,
    authorBandLaneSpacing,
    authorBandStorylineGap,
    getEventVisualPoints,
    getEventsByIds,
    getVisibleSavedHighlightBands,
    aiInsightCandidates,
    activeInsightCandidateId,
    escapeHtml,
    getIdentitySvgMatrix,
    createSvgMatrix,
    toSvgMatrix,
  }
}

function resetAuthorBandEmphasis() {
  resetAuthorBandEmphasisLayer(getAuthorBandRenderContext())
}

function showAuthorBandEmphasis(band) {
  showAuthorBandEmphasisLayer(getAuthorBandRenderContext(), band)
}

function renderHighlightBands() {
  renderHighlightBandsLayer(getAuthorBandRenderContext())
}

function renderActiveInsightBand() {
  renderActiveInsightBandLayer(getAuthorBandRenderContext())
}

function getBandFocusBounds(band) {
  return getBandFocusBoundsFromRender(getAuthorBandRenderContext(), band)
}

function getBandStartFocusBounds(band) {
  return getBandStartFocusBoundsFromRender(getAuthorBandRenderContext(), band)
}

// Include storylines, event icons, and author bands so focus actions near the
// end of the visualization do not get clipped by stale canvas bounds.
function getSvgNavigationBounds() {
  const xs = [0, 14000]
  const ys = [0, 1200]

  currentStorylines.forEach(storyline => {
    if (!Array.isArray(storyline)) return
    storyline.map(getStorylinePoint).forEach(point => {
      if (Number.isFinite(point.x)) xs.push(point.x)
      if (Number.isFinite(point.y)) ys.push(point.y)
    })
  })

  eventVisualsById.forEach(visuals => {
    visuals.forEach(point => {
      if (Number.isFinite(point.x)) xs.push(point.x)
      if (Number.isFinite(point.y)) ys.push(point.y)
    })
  })
  ;[...getVisibleSavedHighlightBands(), ...aiInsightCandidates].forEach(
    band => {
      if (!band || !band.visualBounds) return
      xs.push(
        band.visualBounds.x,
        band.visualBounds.x + band.visualBounds.width
      )
      ys.push(
        band.visualBounds.y,
        band.visualBounds.y + band.visualBounds.height
      )
    }
  )

  if (mySvg && mySvg.getBBox) {
    try {
      const box = mySvg.getBBox()
      if (
        Number.isFinite(box.x) &&
        Number.isFinite(box.width) &&
        box.width > 0
      ) {
        xs.push(box.x, box.x + box.width)
      }
      if (
        Number.isFinite(box.y) &&
        Number.isFinite(box.height) &&
        box.height > 0
      ) {
        ys.push(box.y, box.y + box.height)
      }
    } catch (error) {
      // getBBox can fail while the SVG is still rendering.
    }
  }

  return {
    minX: Math.min(...xs) - 180,
    minY: Math.min(...ys) - 160,
    maxX: Math.max(...xs) + 720,
    maxY: Math.max(...ys) + 260,
  }
}

// Pan/zoom to a story region while staying within the known visualization area.
function focusViewBoxOnBounds(bounds) {
  if (!bounds || typeof setViewBox !== 'function') return
  const navigationBounds = getSvgNavigationBounds()
  const navigationWidth = Math.max(
    900,
    navigationBounds.maxX - navigationBounds.minX
  )
  const navigationHeight = Math.max(
    420,
    navigationBounds.maxY - navigationBounds.minY
  )
  const viewportAspect =
    mySvg && mySvg.clientWidth && mySvg.clientHeight
      ? mySvg.clientWidth / mySvg.clientHeight
      : 14000 / 1200
  let width = bounds.width
  let height = bounds.height
  const boundsAspect = width / height
  if (boundsAspect > viewportAspect) {
    height = width / viewportAspect
  } else {
    width = height * viewportAspect
  }

  width = Math.min(navigationWidth, Math.max(900, width))
  height = Math.min(navigationHeight, Math.max(420, height))
  const centerX = bounds.x + bounds.width / 2
  const centerY = bounds.y + bounds.height / 2
  const x = Math.max(
    navigationBounds.minX,
    Math.min(navigationBounds.maxX - width, centerX - width / 2)
  )
  const y = Math.max(
    navigationBounds.minY,
    Math.min(navigationBounds.maxY - height, centerY - height / 2)
  )
  setViewBox(x, y, width, height)
}

function navigateToSavedSnippet(snippetId) {
  const snippet = savedStorySnippets.find(item => item.id === snippetId)
  if (!snippet) return
  const band = savedHighlightBands.find(item => item.id === snippet.bandId)
  if (!band) return
  focusViewBoxOnBounds(getBandFocusBounds(band))
  showAuthorBandEmphasis(band)
  window.setTimeout(resetAuthorBandEmphasis, 1200)
  setAuthoringStatus(`Focused "${snippet.label}" in the visualization.`)
}

function getEventsByIds(eventIds = []) {
  return eventIds.map(eventId => eventById.get(eventId)).filter(Boolean)
}

// -----------------------------------------------------------------------------
// Local and GPT-generated story insight candidates
// -----------------------------------------------------------------------------

// Local insight mining is the fallback when no LLM endpoint is configured.
function buildLocalInsightSummary(events, label, reason) {
  const fallback = buildStoryLikeFallback(events)
  return fallback.paragraph || reason
}

function createInsightCandidate(
  events,
  label,
  title,
  reason,
  confidence = 0.62
) {
  const sortedEvents = events
    .filter(Boolean)
    .sort((a, b) => a.timestamp - b.timestamp)
  if (sortedEvents.length < 2) return null
  const tag = cleanDraftText(label || 'Story Insight', 56)
  const colorChoice = resolveBandColor(null, '', tag, [])
  const summary = buildLocalInsightSummary(sortedEvents, tag, reason)
  return {
    id: `insight_${Date.now()}_${Math.random()
      .toString(36)
      .slice(2, 8)}`,
    source: 'local-rule',
    status: 'suggested',
    tag,
    title: cleanDraftText(title || tag, 90),
    summary,
    characters: Array.from(
      new Set(sortedEvents.flatMap(event => getEventCharacters(event)))
    ),
    startTimestamp: Math.min(...sortedEvents.map(event => event.timestamp)),
    endTimestamp: Math.max(...sortedEvents.map(event => event.timestamp)),
    evidenceEventIds: sortedEvents.map(event => event._eventId),
    confidence,
    color: colorChoice.color,
    colorSource: 'fallback',
    colorRationale: colorChoice.colorRationale,
    visualBounds: getSelectionBounds(sortedEvents),
    reason,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  }
}

function createEventWindow(events, centerIndex, before = 3, after = 3) {
  const start = Math.max(0, centerIndex - before)
  const end = Math.min(events.length, centerIndex + after + 1)
  return events.slice(start, end)
}

// Candidate generation favors narratively salient event clusters that can become
// authoring bands without requiring GPT.
function mineLocalStoryInsights() {
  const events = allEventData
    .filter(event => event && event._eventId && event.eventType !== 'Moving_In')
    .sort((a, b) => a.timestamp - b.timestamp)
  const candidates = []
  const candidateKeys = new Set()

  function addCandidate(candidate) {
    if (!candidate || !candidate.visualBounds) return
    const key = candidate.evidenceEventIds
      .slice()
      .sort()
      .join('|')
    if (!key || candidateKeys.has(key)) return
    candidateKeys.add(key)
    candidates.push(candidate)
  }

  const familyEvents = events.filter(
    event => event.eventType === 'Family Interaction'
  )
  if (familyEvents.length >= 2) {
    addCandidate(
      createInsightCandidate(
        familyEvents.slice(0, 7),
        'Family Dynamics',
        'Family relationships come into focus',
        'Cluster of Family Interaction events.',
        0.72
      )
    )
  }

  events.forEach((event, index) => {
    if (event.eventType === 'Death') {
      addCandidate(
        createInsightCandidate(
          createEventWindow(events, index, 5, 0),
          'Major Turning Point',
          'A death reshapes the story',
          'Death event with preceding context.',
          0.84
        )
      )
    }
    if (event.eventType === 'Choice') {
      addCandidate(
        createInsightCandidate(
          createEventWindow(events, index, 3, 3),
          'Decision Point',
          'A choice redirects the storyline',
          'Choice event with nearby context.',
          0.68
        )
      )
    }
  })

  const pairGroups = new Map()
  events.forEach(event => {
    const characters = getEventCharacters(event).sort()
    if (characters.length < 2) return
    const key = characters.slice(0, 3).join('|')
    if (!pairGroups.has(key)) pairGroups.set(key, [])
    pairGroups.get(key).push(event)
  })
  Array.from(pairGroups.entries())
    .filter(([, group]) => group.length >= 3)
    .sort((a, b) => b[1].length - a[1].length)
    .slice(0, 3)
    .forEach(([key, group]) => {
      const names = key.split('|').join(', ')
      const hasConflict = group.some(event =>
        /fight|argument|angry|mock|death/i.test(event.eventDetails || '')
      )
      addCandidate(
        createInsightCandidate(
          group.slice(0, 7),
          hasConflict ? 'Relationship Tension' : 'Relationship Thread',
          `${names} share repeated story moments`,
          'Repeated interactions between the same characters.',
          hasConflict ? 0.76 : 0.64
        )
      )
    })

  const salienceEvents = events.filter(event =>
    ['System_Sim_Status', 'Death', 'Choice'].includes(event.eventType)
  )
  if (salienceEvents.length >= 4) {
    addCandidate(
      createInsightCandidate(
        salienceEvents.slice(-7),
        'Late Story Escalation',
        'High-salience events gather near the end',
        'Cluster of high-salience system, choice, or death events.',
        0.66
      )
    )
  }

  return candidates.sort((a, b) => b.confidence - a.confidence).slice(0, 6)
}

function getEventOrderById(events) {
  const orderById = new Map()
  events.forEach((event, index) => {
    orderById.set(event._eventId, index)
  })
  return orderById
}

function normalizeInsightEvidenceIds(rawIds, events) {
  if (!Array.isArray(rawIds)) return []
  const allowedIds = new Set(events.map(event => event._eventId))
  return Array.from(
    new Set(
      rawIds.map(id => String(id || '').trim()).filter(id => allowedIds.has(id))
    )
  ).slice(0, 10)
}

// GPT may return a thematically valid but visually huge arc. Compacting evidence
// keeps preview bands readable and local to one storyline region.
function compactInsightEvidenceIds(rawIds, events, warnings) {
  const ids = normalizeInsightEvidenceIds(rawIds, events)
  if (ids.length < 2) return ids

  const orderById = getEventOrderById(events)
  const sortedIds = ids
    .map(id => ({ id, event: eventById.get(id) }))
    .filter(item => item.event)
    .sort((a, b) => {
      const orderA = orderById.get(a.id)
      const orderB = orderById.get(b.id)
      return orderA - orderB || a.event.timestamp - b.event.timestamp
    })
    .map(item => item.id)
  const spanForIds = candidateIds => {
    const candidateEvents = candidateIds
      .map(id => eventById.get(id))
      .filter(Boolean)
    const orders = candidateIds
      .map(id => orderById.get(id))
      .filter(Number.isFinite)
    const timestamps = candidateEvents
      .map(event => Number(event.timestamp))
      .filter(Number.isFinite)
    return {
      orderSpan: Math.max(...orders) - Math.min(...orders),
      timeSpan: Math.max(...timestamps) - Math.min(...timestamps),
    }
  }

  const wholeSpan = spanForIds(sortedIds)
  if (
    wholeSpan.timeSpan <= aiInsightMaxBandTimeSpan &&
    wholeSpan.orderSpan <= aiInsightMaxBandOrderSpan
  ) {
    return sortedIds.slice(0, 8)
  }

  let bestIds = []
  let bestScore = -Infinity
  for (let start = 0; start < sortedIds.length; start += 1) {
    for (let end = start + 1; end < sortedIds.length; end += 1) {
      const candidateIds = sortedIds.slice(start, end + 1)
      const span = spanForIds(candidateIds)
      if (
        span.timeSpan > aiInsightMaxBandTimeSpan ||
        span.orderSpan > aiInsightMaxBandOrderSpan
      ) {
        continue
      }
      const score =
        candidateIds.length * 100000 - span.timeSpan - span.orderSpan * 1000
      if (score > bestScore) {
        bestScore = score
        bestIds = candidateIds
      }
    }
  }

  if (bestIds.length >= 2) {
    warnings.push(
      'Evidence was compacted to a nearby local beat because GPT selected a very wide arc.'
    )
    return bestIds.slice(0, 8)
  }

  warnings.push(
    'Candidate was dropped because its evidence was too widely spread.'
  )
  return []
}

function isGenericInsightLabel(label) {
  const clean = cleanDraftText(label, 80).toLowerCase()
  return (
    !clean ||
    /^(arc|candidate|insight|story|thread|beat)\s*[a-z0-9]*$/i.test(clean) ||
    /^arc\s+[a-z]$/i.test(clean) ||
    /^story\s+(arc|thread|beat)\s*[a-z0-9]*$/i.test(clean)
  )
}

// Replace generic labels like "Arc C" with readable story-facing names.
function deriveNaturalInsightLabel(rawCandidate, evidenceEvents, summary) {
  const title = cleanDraftText(rawCandidate && rawCandidate.title, 56)
  if (title && !isGenericInsightLabel(title)) return title

  const text = [
    summary,
    ...evidenceEvents.map(event => event.eventDetails || ''),
    ...evidenceEvents.map(event => event.eventType || ''),
  ]
    .join(' ')
    .toLowerCase()
  if (/career|job|work|technician|promotion|business/.test(text)) {
    return 'Career Kickoff'
  }
  if (/romance|flirt|date|kiss|crush/.test(text)) return 'New Romance'
  if (/fight|argument|angry|mock|conflict|tension|clash/.test(text)) {
    return 'Family Conflict'
  }
  if (/joke|banter|playful|comedy|silly|laugh/.test(text)) {
    return 'Playful Banter'
  }
  if (/death|died|grave|loss|mourning|inheritance|bequeath/.test(text)) {
    return 'Family Loss'
  }
  if (/invite|host|meal|cook|gather|family|unity/.test(text)) {
    return 'Family Gathering'
  }
  const characters = Array.from(
    new Set(evidenceEvents.flatMap(event => getEventCharacters(event)))
  ).slice(0, 2)
  return characters.length >= 2
    ? `${characters[0]} and ${characters[1]}`
    : 'Story Moment'
}

function annotateInsightOverlaps(candidates) {
  const ownersByEventId = new Map()
  candidates.forEach(candidate => {
    const overlappingOwners = new Set()
    const evidenceEventIds = candidate.evidenceEventIds || []
    evidenceEventIds.forEach(eventId => {
      const owners = ownersByEventId.get(eventId) || []
      owners.forEach(owner => overlappingOwners.add(owner))
      ownersByEventId.set(eventId, owners.concat(candidate.tag))
    })
    if (!overlappingOwners.size) return
    const overlapText = `Overlap note: shares evidence with ${Array.from(
      overlappingOwners
    ).join(', ')}; review whether this is shared context or duplication.`
    candidate.reason = candidate.reason
      ? `${candidate.reason} ${overlapText}`
      : overlapText
  })
  return candidates
}

// Validate full-log AI Story Insight output before showing candidates in the UI.
function validateAIInsightResponse(rawResponse, events) {
  if (!rawResponse || typeof rawResponse !== 'object') {
    throw new Error('LLM insight response was not a JSON object')
  }
  const rawCandidates = Array.isArray(rawResponse.candidates)
    ? rawResponse.candidates
    : []
  const model = cleanDraftText(rawResponse.model, 80) || 'external'
  const candidates = rawCandidates
    .map((rawCandidate, index) => {
      const warnings = []
      const summary = cleanDraftText(
        rawCandidate &&
          (rawCandidate.paragraph ||
            rawCandidate.summary ||
            rawCandidate.description),
        1400
      )
      const evidenceEventIds = compactInsightEvidenceIds(
        rawCandidate && rawCandidate.evidenceEventIds,
        events,
        warnings
      )
      const evidenceEvents = getEventsByIds(evidenceEventIds).sort(
        (a, b) => a.timestamp - b.timestamp
      )
      const rawLabel = cleanDraftText(
        rawCandidate && (rawCandidate.label || rawCandidate.tag),
        56
      )
      const label = isGenericInsightLabel(rawLabel)
        ? deriveNaturalInsightLabel(rawCandidate, evidenceEvents, summary)
        : rawLabel
      if (!label || !summary || evidenceEvents.length < 2) return null
      if (
        rawCandidate &&
        Array.isArray(rawCandidate.evidenceEventIds) &&
        evidenceEventIds.length < rawCandidate.evidenceEventIds.length
      ) {
        warnings.push(
          'Some LLM evidence ids were not in the dataset and were removed.'
        )
      }
      const colorChoice = resolveBandColor(
        rawCandidate && rawCandidate.color,
        rawCandidate && rawCandidate.colorRationale,
        label,
        warnings
      )
      const reasonParts = [
        cleanDraftText(rawCandidate && rawCandidate.reason, 260),
        cleanDraftText(rawCandidate && rawCandidate.overlapExplanation, 260),
        warnings.join(' '),
      ].filter(Boolean)
      return {
        id: `insight_llm_${Date.now()}_${index}`,
        source: 'llm-insight',
        status: 'suggested',
        tag: label,
        title: cleanDraftText(rawCandidate && rawCandidate.title, 90) || label,
        summary,
        characters: Array.from(
          new Set(evidenceEvents.flatMap(event => getEventCharacters(event)))
        ),
        startTimestamp: Math.min(
          ...evidenceEvents.map(event => event.timestamp)
        ),
        endTimestamp: Math.max(...evidenceEvents.map(event => event.timestamp)),
        evidenceEventIds: evidenceEvents.map(event => event._eventId),
        confidence: clampConfidence(rawCandidate && rawCandidate.confidence),
        color: colorChoice.color,
        colorSource: colorChoice.colorSource,
        colorRationale: colorChoice.colorRationale,
        visualBounds: getSelectionBounds(evidenceEvents),
        reason: reasonParts.join(' '),
        model,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      }
    })
    .filter(Boolean)
    .filter(candidate => candidate.visualBounds)
    .sort((a, b) => b.confidence - a.confidence)
    .slice(0, 6)
  return annotateInsightOverlaps(candidates)
}

function setAIInsightPanelVisible(visible) {
  setAIInsightPanelVisibleDom(visible)
}

function isAIInsightPanelVisible() {
  return isAIInsightPanelVisibleDom()
}

// -----------------------------------------------------------------------------
// AI Story Insight floating panel
// -----------------------------------------------------------------------------

function closeAIInsightPanel() {
  setAIInsightPanelVisible(false)
  activeInsightCandidateId = null
  renderHighlightBands()
}

// The top-level button toggles the floating panel. Refreshing candidates happens
// from inside the panel so users can request a new batch explicitly.
function handleAIStoryInsightButtonClick() {
  if (isAIInsightPanelVisible()) {
    closeAIInsightPanel()
    return
  }
  setAIInsightPanelVisible(true)
  updateAIInsightPanel()
  renderHighlightBands()
  if (!aiInsightCandidates.length) {
    runAIStoryInsightScan()
  }
}

function setAIInsightStatus(message) {
  setAIInsightPanelStatus(message)
}

// Insight cards are editable before acceptance; changes update the active
// preview band immediately so the author can judge the final label/color.
function updateAIInsightCandidateDraft(candidateId, field, value) {
  const candidate = aiInsightCandidates.find(item => item.id === candidateId)
  if (!candidate) return

  if (field === 'tag') {
    const label = cleanDraftText(value, 56)
    candidate.tag = label || 'Story Moment'
    candidate.title = candidate.tag
  } else if (field === 'summary') {
    candidate.summary = cleanDraftText(value, 1400)
  } else if (field === 'color') {
    const color = normalizeHexColor(value)
    if (!color) return
    candidate.color = color
    candidate.colorSource = 'user'
    candidate.colorRationale =
      'User-edited color from the AI Story Insight draft.'
  } else {
    return
  }

  candidate.authorEdited = true
  candidate.updatedAt = new Date().toISOString()
  if (candidate.id === activeInsightCandidateId) {
    renderHighlightBands()
  }
}

// Render the panel body. Window chrome, dragging, close, and refresh controls
// are bound in ui/ai-insight-panel.js.
function updateAIInsightPanel() {
  const countEl = document.getElementById('aiInsightCount')
  const listEl = document.getElementById('aiInsightList')
  if (countEl) {
    countEl.textContent = `${aiInsightCandidates.length} insight${
      aiInsightCandidates.length === 1 ? '' : 's'
    }`
  }
  if (!listEl) return
  listEl.innerHTML = aiInsightCandidates.length
    ? aiInsightCandidates
        .map(candidate => {
          const events = getEventsByIds(candidate.evidenceEventIds)
          const isActive = candidate.id === activeInsightCandidateId
          const sourceLabel =
            candidate.source === 'llm-insight'
              ? `GPT scan${candidate.model ? ` (${candidate.model})` : ''}`
              : 'Local scan'
          const color =
            normalizeHexColor(candidate.color) ||
            getFallbackBandColor(candidate.tag)
          return `
            <div class="ai-insight-row ${
              isActive ? 'is-active' : ''
            }" data-candidate-id="${escapeHtml(candidate.id)}">
              <div class="ai-insight-row-header">
                <label class="ai-insight-edit-field">
                  Name
                  <input
                    type="text"
                    value="${escapeHtml(candidate.tag)}"
                    data-insight-field="tag"
                    data-candidate-id="${escapeHtml(candidate.id)}"
                  />
                </label>
                <span class="ai-insight-meta">${Math.round(
                  (candidate.confidence || 0.5) * 100
                )}%</span>
              </div>
              <label class="ai-insight-edit-field">
                Story summary
                <textarea
                  rows="4"
                  data-insight-field="summary"
                  data-candidate-id="${escapeHtml(candidate.id)}"
                >${escapeHtml(candidate.summary)}</textarea>
              </label>
              <label class="ai-insight-edit-field compact">
                Band color
                <input
                  type="color"
                  value="${escapeHtml(color)}"
                  data-insight-field="color"
                  data-candidate-id="${escapeHtml(candidate.id)}"
                />
              </label>
              <div class="ai-insight-meta">${escapeHtml(
                sourceLabel
              )}. ${escapeHtml(candidate.reason || '')} ${
            events.length
          } evidence events.</div>
              <div class="ai-insight-actions">
                <button type="button" data-action="show" data-candidate-id="${escapeHtml(
                  candidate.id
                )}">Show</button>
                <button type="button" data-action="accept" data-candidate-id="${escapeHtml(
                  candidate.id
                )}">Accept</button>
                <button type="button" data-action="reject" data-candidate-id="${escapeHtml(
                  candidate.id
                )}">Reject</button>
              </div>
            </div>
          `
        })
        .join('')
    : '<div class="authoring-empty">No AI story insights yet.</div>'
}

// Run a fresh full-log scan. If GPT is configured but fails, do not silently mix
// in local results; the user needs to know which source created each candidate.
async function runAIStoryInsightScan() {
  setAIInsightPanelVisible(true)
  if (!allEventData.length) {
    setAIInsightStatus('Event data is still loading. Try again in a moment.')
    updateAIInsightPanel()
    return
  }
  const insightBtn = document.getElementById('aiStoryInsightBtn')
  const refreshBtn = document.getElementById('aiInsightRefreshBtn')
  if (insightBtn) insightBtn.disabled = true
  if (refreshBtn) refreshBtn.disabled = true
  aiInsightCandidates = []
  activeInsightCandidateId = null
  const endpoint = getStoryInsightEndpoint(
    getConfiguredLLMEndpoint(),
    window.location.href
  )
  setAIInsightStatus(
    endpoint
      ? 'Scanning the full event dataset with GPT...'
      : 'No GPT endpoint configured. Showing local candidate mining.'
  )
  updateAIInsightPanel()
  renderHighlightBands()

  if (endpoint) {
    try {
      const insightEvents = allEventData
        .filter(event => event && event._eventId)
        .sort((a, b) => a.timestamp - b.timestamp)
      const result = await requestAIStoryInsightCandidates({
        endpoint,
        events: insightEvents,
        maxBandTimeSpan: aiInsightMaxBandTimeSpan,
        maxBandOrderSpan: aiInsightMaxBandOrderSpan,
        matchId: ACTIVE_DATASET.matchId,
        storyContext: getStorySynopsisContext(),
        toInsightLLMEvent,
        validateAIInsightResponse,
        cleanDraftText,
        createTimeoutSignal,
      })
      aiInsightCandidates = result.candidates
      if (aiInsightCandidates.length) {
        showAIInsightCandidate(aiInsightCandidates[0].id, false, 'band-start')
        setAIInsightStatus(
          `Generated ${aiInsightCandidates.length} GPT story insight${
            aiInsightCandidates.length === 1 ? '' : 's'
          }. Showing the start of the first suggested band.`
        )
        return
      }
      setAIInsightStatus(
        'GPT scan returned no usable bands. No local fallback was shown, so this result is not mixed with local suggestions.'
      )
      return
    } catch (error) {
      console.error(error)
      aiInsightCandidates = []
      activeInsightCandidateId = null
      updateAIInsightPanel()
      renderHighlightBands()
      setAIInsightStatus(
        `GPT scan failed: ${cleanDraftText(
          error && error.message,
          180
        )}. Restart the LLM server after pulling these changes, then try again.`
      )
      return
    } finally {
      if (insightBtn) insightBtn.disabled = false
      if (refreshBtn) refreshBtn.disabled = false
    }
  }
  if (insightBtn) insightBtn.disabled = false
  if (refreshBtn) refreshBtn.disabled = false

  aiInsightCandidates = mineLocalStoryInsights()
  if (aiInsightCandidates.length) {
    showAIInsightCandidate(aiInsightCandidates[0].id, false, 'band-start')
    setAIInsightStatus(
      'Generated local candidate arcs. Showing the start of the first suggested band.'
    )
    return
  }
  setAIInsightStatus('No candidate arcs found from the current event data.')
  updateAIInsightPanel()
  renderHighlightBands()
}

// Show a candidate in the SVG and optionally navigate to its band or band start.
function showAIInsightCandidate(
  candidateId,
  updateStatus = true,
  focusMode = 'band-start'
) {
  const candidate = aiInsightCandidates.find(item => item.id === candidateId)
  if (!candidate) return
  const evidenceEvents = getEventsByIds(candidate.evidenceEventIds)
  candidate.visualBounds =
    getSelectionBounds(evidenceEvents) || candidate.visualBounds
  activeInsightCandidateId = candidateId
  renderHighlightBands()
  focusViewBoxOnBounds(
    focusMode === 'band-start'
      ? getBandStartFocusBounds(candidate) || getBandFocusBounds(candidate)
      : getBandFocusBounds(candidate)
  )
  showAuthorBandEmphasis(candidate)
  window.setTimeout(resetAuthorBandEmphasis, 1200)
  updateAIInsightPanel()
  if (updateStatus) {
    setAIInsightStatus(`Showing "${candidate.tag}" in the visualization.`)
  }
}

// Accepting an insight promotes the suggestion into the same saved-band and
// saved-snippet structures used by manual authoring.
function acceptAIInsightCandidate(candidateId) {
  const candidate = aiInsightCandidates.find(item => item.id === candidateId)
  if (!candidate) return
  const label = cleanDraftText(candidate.tag, 56) || 'Story Moment'
  const summary =
    cleanDraftText(candidate.summary, 1400) || summarizeEditedParagraph(label)
  const color =
    normalizeHexColor(candidate.color) || getFallbackBandColor(label)
  const bandId = `band_${Date.now()}`
  const snippetId = `snippet_${Date.now()}`
  const acceptedBand = {
    ...candidate,
    id: bandId,
    layerId: activeAuthoringLayerId,
    source: candidate.source || 'ai-insight',
    status: 'accepted',
    model: candidate.model || lastSummaryModel || '',
    tag: label,
    title: label,
    summary,
    color,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  }
  savedHighlightBands.push(acceptedBand)
  savedStorySnippets.push({
    id: snippetId,
    bandId,
    layerId: activeAuthoringLayerId,
    model: candidate.model || lastSummaryModel || '',
    label,
    oneSentenceSummary: summarizeEditedParagraph(summary),
    paragraph: summary,
    evidenceEventIds: candidate.evidenceEventIds,
    authorEdited: Boolean(candidate.authorEdited),
  })
  aiInsightCandidates = aiInsightCandidates.filter(
    item => item.id !== candidateId
  )
  activeInsightCandidateId = null
  persistAuthoringAnnotations()
  updateAuthoringPanel()
  updateAIInsightPanel()
  renderHighlightBands()
  setAIInsightStatus(`Accepted "${candidate.tag}" as a saved snippet.`)
}

// Rejected suggestions are preserved in the audit log for later review.
function rejectAIInsightCandidate(candidateId) {
  const candidate = aiInsightCandidates.find(item => item.id === candidateId)
  if (!candidate) return
  auditLog.push({
    id: `audit_${Date.now()}`,
    layerId: activeAuthoringLayerId,
    action: 'rejected',
    targetType: 'aiStoryInsight',
    targetId: candidateId,
    reason: 'User rejected an AI Story Insight candidate.',
    timestamp: new Date().toISOString(),
    snapshot: {
      tag: candidate.tag,
      summary: candidate.summary,
      evidenceEventIds: candidate.evidenceEventIds,
    },
  })
  aiInsightCandidates = aiInsightCandidates.filter(
    item => item.id !== candidateId
  )
  if (activeInsightCandidateId === candidateId) activeInsightCandidateId = null
  persistAuthoringAnnotations()
  updateAIInsightPanel()
  renderHighlightBands()
  setAIInsightStatus(`Rejected "${candidate.tag}".`)
}

// -----------------------------------------------------------------------------
// Manual authoring panel and annotation actions
// -----------------------------------------------------------------------------

// Render the right-side authoring controls from the current selection, draft,
// saved snippets, and active layer state.
function updateAuthoringPanel() {
  ensureAuthoringLayers()
  const selectedEvents = getSelectedEvents()
  const countEl = document.getElementById('selectedEventCount')
  const listEl = document.getElementById('selectedEventsList')
  const statusEl = document.getElementById('authoringStatus')
  const summarizeBtn = document.getElementById('summarizeSelectionBtn')
  const saveBtn = document.getElementById('saveDraftBtn')
  const labelInput = document.getElementById('storyDraftLabel')
  const summaryInput = document.getElementById('storyDraftSummary')
  const colorInput = document.getElementById('storyDraftColor')
  const savedList = document.getElementById('savedSnippetsList')
  const layerSelect = document.getElementById('authoringLayerSelect')
  if (!countEl || !listEl) return

  const activeLayer = getActiveAuthoringLayer()
  const activeSnippets = getVisibleSavedStorySnippets()

  if (layerSelect) {
    layerSelect.innerHTML = authoringLayers
      .map(
        layer => `
          <option value="${escapeHtml(layer.id)}">${escapeHtml(
          layer.name
        )}</option>
        `
      )
      .join('')
    layerSelect.value = activeAuthoringLayerId
    layerSelect.title = `Active layer: ${activeLayer ? activeLayer.name : ''}`
  }

  countEl.textContent = `${selectedEvents.length} selected`
  listEl.innerHTML = selectedEvents.length
    ? selectedEvents
        .map(
          event => `
            <div class="authoring-event-row">
              <strong>${escapeHtml(event.eventType)}</strong>
              <span>${escapeHtml(formatEventTime(event))}</span>
              <p>${escapeHtml(event.eventDetails)}</p>
            </div>
          `
        )
        .join('')
    : '<div class="authoring-empty">Select event icons in the visualization.</div>'

  if (summarizeBtn) summarizeBtn.disabled = selectedEvents.length === 0
  if (saveBtn) saveBtn.disabled = !currentStoryDraft

  if (!currentStoryDraft) {
    if (labelInput) labelInput.value = ''
    if (summaryInput) summaryInput.value = ''
    if (colorInput) colorInput.value = getFallbackBandColor('Story Moment')
  } else {
    if (labelInput) labelInput.value = currentStoryDraft.label || ''
    if (summaryInput) summaryInput.value = currentStoryDraft.paragraph || ''
    if (colorInput) {
      colorInput.value =
        normalizeHexColor(currentStoryDraft.color) ||
        getFallbackBandColor(currentStoryDraft.label)
    }
  }

  if (savedList) {
    savedList.innerHTML = activeSnippets.length
      ? activeSnippets
          .map(snippet => {
            const band = savedHighlightBands.find(
              item => item.id === snippet.bandId
            )
            const bandColor = band
              ? normalizeHexColor(band.color) || getFallbackBandColor(band.tag)
              : getFallbackBandColor(snippet.label)
            if (snippet.id === editingSavedSnippetId) {
              return `
                <div
                  class="authoring-saved-row is-editing"
                  data-snippet-id="${escapeHtml(snippet.id)}"
                >
                  <label class="authoring-field saved-snippet-edit-field">
                    Label
                    <input
                      class="saved-snippet-label-input"
                      type="text"
                      value="${escapeHtml(snippet.label)}"
                    />
                  </label>
                  <label class="authoring-field saved-snippet-edit-field">
                    Story summary
                    <textarea
                      class="saved-snippet-paragraph-input"
                      rows="5"
                    >${escapeHtml(snippet.paragraph)}</textarea>
                  </label>
                  <label class="authoring-field saved-snippet-edit-field">
                    Band color
                    <input
                      class="saved-snippet-color-input"
                      type="color"
                      value="${escapeHtml(bandColor)}"
                      aria-label="Change color for ${escapeHtml(snippet.label)}"
                    />
                  </label>
                  <div class="saved-snippet-edit-actions">
                    <button
                      class="save-snippet-edit-btn"
                      type="button"
                      data-snippet-id="${escapeHtml(snippet.id)}"
                    >
                      Save
                    </button>
                    <button
                      class="cancel-snippet-edit-btn"
                      type="button"
                      data-snippet-id="${escapeHtml(snippet.id)}"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              `
            }
            return `
              <div
                class="authoring-saved-row"
                data-snippet-id="${escapeHtml(snippet.id)}"
                title="Click to focus this snippet in the visualization"
              >
                <div class="authoring-saved-row-header">
                  <strong>${escapeHtml(snippet.label)}</strong>
                  <div class="authoring-saved-row-actions">
                    <button
                      class="edit-snippet-btn"
                      type="button"
                      data-snippet-id="${escapeHtml(snippet.id)}"
                    >
                      Edit
                    </button>
                    <button
                      class="remove-snippet-btn"
                      type="button"
                      data-snippet-id="${escapeHtml(snippet.id)}"
                    >
                      Remove
                    </button>
                  </div>
                </div>
                <p>${escapeHtml(snippet.paragraph)}</p>
                <div class="authoring-color-row">
                  <span>Band color</span>
                  <input
                    class="saved-band-color-input"
                    type="color"
                    value="${escapeHtml(bandColor)}"
                    data-band-id="${escapeHtml(snippet.bandId)}"
                    aria-label="Change color for ${escapeHtml(snippet.label)}"
                  />
                </div>
              </div>
            `
          })
          .join('')
      : `<div class="authoring-empty">No saved snippets in ${
          activeLayer ? escapeHtml(activeLayer.name) : 'this layer'
        } yet.</div>`
  }

  if (statusEl && !statusEl.textContent) {
    statusEl.textContent = authoringSelectionMode
      ? 'Selection mode is on.'
      : 'Turn on Author mode to select events.'
  }
}

function setAuthoringStatus(message) {
  const statusEl = document.getElementById('authoringStatus')
  if (statusEl) statusEl.textContent = message
}

// When no LLM endpoint is available, keep authoring usable with a local,
// chronological narrative draft.
function buildStoryLikeFallback(events) {
  const characters = Array.from(
    new Set(events.flatMap(event => getEventCharacters(event)))
  )
  const mainCharacters = characters.slice(0, 3).join(', ')
  const eventTypes = Array.from(new Set(events.map(event => event.eventType)))
  const first = events[0]
  const last = events[events.length - 1]
  const label = eventTypes.includes('Family Interaction')
    ? 'Family Bond'
    : eventTypes.includes('Choice')
    ? 'Turning Point'
    : eventTypes.includes('Death')
    ? 'Major Loss'
    : eventTypes.includes('Chat') || eventTypes.includes('Contact')
    ? 'Social Shift'
    : 'Story Moment'
  const oneSentenceSummary = `${mainCharacters ||
    'The selected characters'} move through ${
    events.length
  } connected story moment${
    events.length === 1 ? '' : 's'
  }, from ${formatEventTime(first)} to ${formatEventTime(last)}.`
  const eventDetails = events
    .slice(0, 4)
    .map(event => event.eventDetails)
    .filter(Boolean)
  const paragraph = eventDetails.length
    ? `${mainCharacters ||
        'The selected characters'} enter this part of the story through ${
        eventDetails[0]
      } ${
        eventDetails[1] ? `The moment then develops as ${eventDetails[1]}` : ''
      } ${eventDetails[2] ? `Soon after, ${eventDetails[2]}` : ''}${
        events.length > 3
          ? ' Together, the selected events turn these scattered actions into a small narrative arc.'
          : ''
      }`
    : oneSentenceSummary
  return {
    label,
    oneSentenceSummary,
    paragraph,
    alternativeLabels: ['Story Beat', 'Character Arc', 'Relationship Moment'],
    evidenceEventIds: events.map(event => event._eventId),
    color: getFallbackBandColor(label),
    colorSource: 'fallback',
    colorRationale:
      'Fallback color selected for contrast and character-line separation.',
    model: 'local-fallback',
  }
}

// Request payloads are intentionally smaller than raw events so prompts stay
// focused on story interpretation instead of raw data shape.
function toLLMEvent(event) {
  return {
    id: event._eventId,
    timestamp: event.timestamp,
    originalTime: event.originalTime,
    eventType: event.eventType,
    characters: getEventCharacters(event),
    interactor: event.interactor,
    interactee: event.interactee,
    location: event.position,
    details: event.eventDetails,
  }
}

function toInsightLLMEvent(event, index) {
  return {
    id: event._eventId,
    order: index + 1,
    timestamp: event.timestamp,
    eventType: event.eventType,
    characters: getEventCharacters(event),
    interactor: event.interactor,
    interactee: event.interactee,
    location: event.position,
    details: cleanDraftText(event.eventDetails, 240),
    hasVideoTime: Boolean(
      event.originalTime && VIDEO_EVENT_TYPES.includes(event.eventType)
    ),
  }
}

// Use the Story modal text as global context for full-log AI Story Insight.
function getStorySynopsisContext() {
  const modal = document.getElementById('storySynopsisModal')
  if (!modal) return ''
  const paragraphs = Array.from(modal.querySelectorAll('p'))
    .map(paragraph => paragraph.textContent)
    .join(' ')
  return cleanDraftText(paragraphs, 1000)
}

// Generate or fallback-fill the editable draft for the current selection.
async function summarizeSelection() {
  const events = getSelectedEvents()
  if (!events.length) return
  setAuthoringStatus('Generating story draft...')
  try {
    currentStoryDraft = await requestStorySummary({
      events,
      endpoint: getConfiguredLLMEndpoint(),
      toLLMEvent,
      validateStoryDraft,
      buildStoryLikeFallback,
    })
    lastSummaryModel = currentStoryDraft.model || 'external'
    setAuthoringStatus(
      currentStoryDraft.model === 'local-fallback'
        ? 'Generated a local draft. Configure an LLM endpoint to use GPT.'
        : currentStoryDraft.validationWarnings &&
          currentStoryDraft.validationWarnings.length
        ? `Generated story draft. Validation adjusted ${currentStoryDraft.validationWarnings.length} field.`
        : 'Generated story draft with the configured LLM endpoint.'
    )
    updateAuthoringPanel()
  } catch (error) {
    console.error(error)
    currentStoryDraft = buildStoryLikeFallback(events)
    setAuthoringStatus('LLM request failed, so a local draft was generated.')
    updateAuthoringPanel()
  }
}

// Save the current editable draft as both a visual band and a text snippet.
function saveCurrentDraft() {
  const events = getSelectedEvents()
  if (!events.length || !currentStoryDraft) return
  const labelInput = document.getElementById('storyDraftLabel')
  const summaryInput = document.getElementById('storyDraftSummary')
  const colorInput = document.getElementById('storyDraftColor')
  const label = labelInput ? labelInput.value.trim() : currentStoryDraft.label
  const paragraph = summaryInput
    ? summaryInput.value.trim()
    : currentStoryDraft.paragraph
  const editedOneSentenceSummary = summarizeEditedParagraph(paragraph)
  const evidenceEventIds = events.map(event => event._eventId)
  const bounds = getSelectionBounds(events)
  const draftColor = normalizeHexColor(currentStoryDraft.color)
  const savedColor =
    normalizeHexColor(colorInput && colorInput.value) ||
    draftColor ||
    getFallbackBandColor(label || currentStoryDraft.label)
  const colorWasEdited = draftColor && savedColor !== draftColor
  const bandId = `band_${Date.now()}`
  const snippetId = `snippet_${Date.now()}`
  savedHighlightBands.push({
    id: bandId,
    layerId: activeAuthoringLayerId,
    source: currentStoryDraft.model === 'local-fallback' ? 'local' : 'llm',
    status: 'accepted',
    tag: label || currentStoryDraft.label,
    title: label || currentStoryDraft.label,
    summary: paragraph,
    characters: Array.from(
      new Set(events.flatMap(event => getEventCharacters(event)))
    ),
    startTimestamp: Math.min(...events.map(event => event.timestamp)),
    endTimestamp: Math.max(...events.map(event => event.timestamp)),
    evidenceEventIds,
    confidence: currentStoryDraft.confidence || 0.5,
    model: currentStoryDraft.model || 'local-fallback',
    color: savedColor,
    colorSource: colorWasEdited
      ? 'user'
      : currentStoryDraft.colorSource || 'fallback',
    colorRationale: colorWasEdited
      ? 'User-edited color from the story draft editor.'
      : currentStoryDraft.colorRationale,
    visualBounds: bounds,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  })
  savedStorySnippets.push({
    id: snippetId,
    bandId,
    layerId: activeAuthoringLayerId,
    label: label || currentStoryDraft.label,
    oneSentenceSummary: editedOneSentenceSummary,
    paragraph,
    evidenceEventIds,
    model: currentStoryDraft.model || 'local-fallback',
    authorEdited: true,
  })
  persistAuthoringAnnotations()
  clearEventSelection()
  renderHighlightBands()
  setAuthoringStatus('Saved story snippet and highlight band.')
}

function startEditingSavedSnippet(snippetId) {
  const snippet = savedStorySnippets.find(item => item.id === snippetId)
  if (!snippet) return
  editingSavedSnippetId = snippetId
  updateAuthoringPanel()
  setAuthoringStatus(`Editing "${snippet.label}".`)
}

function cancelEditingSavedSnippet() {
  editingSavedSnippetId = null
  updateAuthoringPanel()
  setAuthoringStatus('Canceled saved snippet edit.')
}

function saveSavedSnippetEdit(snippetId, edits = {}) {
  const snippet = savedStorySnippets.find(item => item.id === snippetId)
  if (!snippet) return
  const band = savedHighlightBands.find(item => item.id === snippet.bandId)
  const previousSnapshot = {
    label: snippet.label,
    paragraph: snippet.paragraph,
    bandId: snippet.bandId,
    bandTag: band ? band.tag : '',
    bandColor: band ? band.color : '',
    evidenceEventIds: snippet.evidenceEventIds || [],
  }
  const label = cleanDraftText(edits.label, 64) || snippet.label
  const paragraph = cleanDraftText(edits.paragraph, 2000) || snippet.paragraph
  const color = normalizeHexColor(edits.color)
  const updatedAt = new Date().toISOString()

  snippet.label = label
  snippet.oneSentenceSummary = summarizeEditedParagraph(paragraph)
  snippet.paragraph = paragraph
  snippet.authorEdited = true
  snippet.updatedAt = updatedAt

  if (band) {
    band.tag = label
    band.title = label
    band.summary = paragraph
    if (color) {
      band.color = color
      band.colorSource = 'user'
      band.colorRationale = 'User-edited color from the saved snippet editor.'
    }
    band.updatedAt = updatedAt
  }

  auditLog.push({
    id: `audit_${Date.now()}`,
    layerId: snippet.layerId || activeAuthoringLayerId,
    action: 'edited',
    targetType: 'storySnippet',
    targetId: snippetId,
    reason: 'User edited a saved story snippet.',
    timestamp: updatedAt,
    snapshot: {
      before: previousSnapshot,
      after: {
        label: snippet.label,
        paragraph: snippet.paragraph,
        bandId: snippet.bandId,
        bandTag: band ? band.tag : '',
        bandColor: band ? band.color : '',
        evidenceEventIds: snippet.evidenceEventIds || [],
      },
    },
  })

  editingSavedSnippetId = null
  persistAuthoringAnnotations()
  renderHighlightBands()
  updateAuthoringPanel()
  setAuthoringStatus(`Updated "${label}".`)
}

// Remove snippet and band together so the side panel and SVG stay synchronized.
function removeSavedSnippet(snippetId) {
  const snippet = savedStorySnippets.find(item => item.id === snippetId)
  if (!snippet) return
  const removedBand = savedHighlightBands.find(
    band => band.id === snippet.bandId
  )

  savedStorySnippets = savedStorySnippets.filter(item => item.id !== snippetId)
  savedHighlightBands = savedHighlightBands.filter(
    band => band.id !== snippet.bandId
  )
  if (editingSavedSnippetId === snippetId) editingSavedSnippetId = null
  auditLog.push({
    id: `audit_${Date.now()}`,
    layerId: snippet.layerId || activeAuthoringLayerId,
    action: 'removed',
    targetType: 'storySnippet',
    targetId: snippetId,
    reason: 'User removed a saved story snippet and its highlight band.',
    timestamp: new Date().toISOString(),
    snapshot: {
      label: snippet.label,
      paragraph: snippet.paragraph,
      bandId: snippet.bandId,
      bandTag: removedBand ? removedBand.tag : '',
      evidenceEventIds: snippet.evidenceEventIds || [],
    },
  })
  persistAuthoringAnnotations()
  renderHighlightBands()
  updateAuthoringPanel()
  setAuthoringStatus('Removed story snippet and highlight band.')
}

function updateSavedBandColor(bandId, rawColor) {
  const color = normalizeHexColor(rawColor)
  if (!color) return
  const band = savedHighlightBands.find(item => item.id === bandId)
  if (!band) return
  band.color = color
  band.colorSource = 'user'
  band.colorRationale = 'User-edited color after saving the story snippet.'
  band.updatedAt = new Date().toISOString()
  auditLog.push({
    id: `audit_${Date.now()}`,
    layerId: band.layerId || activeAuthoringLayerId,
    action: 'edited-color',
    targetType: 'highlightBand',
    targetId: bandId,
    reason: 'User changed the highlight band color.',
    timestamp: new Date().toISOString(),
    snapshot: {
      tag: band.tag,
      color,
      evidenceEventIds: band.evidenceEventIds || [],
    },
  })
  persistAuthoringAnnotations()
  renderHighlightBands()
  setAuthoringStatus('Updated highlight band color.')
}

// Import/export operate on annotation JSON, not the raw gameplay event files.
function downloadAuthoringAnnotations() {
  downloadAnnotationJson(createAnnotationExport())
}

function loadImportedAuthoringAnnotations(data) {
  const normalized = normalizeImportedAuthoringAnnotations(data)
  applyAuthoringStateSnapshot(normalized)
  lastSummaryModel = normalized.lastModel
  editingSavedSnippetId = null
  ensureAuthoringLayers()
  persistAuthoringAnnotations()
  clearEventSelection()
  updateAuthoringPanel()
  renderHighlightBands()
  setAuthoringStatus(
    `Imported ${savedStorySnippets.length} snippet${
      savedStorySnippets.length === 1 ? '' : 's'
    } across ${authoringLayers.length} layer${
      authoringLayers.length === 1 ? '' : 's'
    }.`
  )
}

function importAuthoringAnnotationsFromFile(file) {
  if (!file) return
  const reader = new FileReader()
  reader.onload = event => {
    try {
      const data = JSON.parse(event.target.result || '{}')
      loadImportedAuthoringAnnotations(data)
    } catch (error) {
      console.error(error)
      setAuthoringStatus(`Import failed: ${cleanDraftText(error.message, 140)}`)
    }
  }
  reader.readAsText(file)
}

// Layer actions are local to this prototype; layer normalization lives in
// authoring/layers.js.
function addAuthoringLayer() {
  ensureAuthoringLayers()
  const defaultName = `Layer ${authoringLayers.length + 1}`
  const requestedName =
    typeof window !== 'undefined' && window.prompt
      ? window.prompt('Name this authoring layer', defaultName)
      : defaultName
  if (requestedName === null) return
  const layer = createAuthoringLayer(requestedName || defaultName)
  authoringLayers.push(layer)
  activeAuthoringLayerId = layer.id
  editingSavedSnippetId = null
  persistAuthoringAnnotations()
  updateAuthoringPanel()
  renderHighlightBands()
  setAuthoringStatus(`Created "${layer.name}".`)
}

function switchAuthoringLayer(layerId) {
  ensureAuthoringLayers()
  if (!authoringLayers.some(layer => layer.id === layerId)) return
  activeAuthoringLayerId = layerId
  editingSavedSnippetId = null
  persistAuthoringAnnotations()
  clearEventSelection()
  updateAuthoringPanel()
  renderHighlightBands()
  const layer = getActiveAuthoringLayer()
  setAuthoringStatus(`Showing "${layer.name}".`)
}

function setAuthoringMode(enabled) {
  authoringSelectionMode = enabled
  const btn = document.getElementById('authorModeBtn')
  const panel = document.getElementById('authoringPanel')
  if (btn) btn.textContent = enabled ? 'Author: On' : 'Author: Off'
  if (panel) panel.style.display = enabled ? 'block' : 'none'
  setAuthoringStatus(
    enabled ? 'Selection mode is on.' : 'Turn on Author mode to select events.'
  )
  updateAuthoringPanel()
}

// Bind authoring UI and SVG selection after the SVG and viewBox are initialized.
function initAuthoringTool() {
  loadAuthoringAnnotations()
  bindAuthoringPanelControls({
    onToggleAuthoring: () => setAuthoringMode(!authoringSelectionMode),
    onClearSelection: clearEventSelection,
    onSummarize: summarizeSelection,
    onSaveDraft: saveCurrentDraft,
    onDownload: downloadAuthoringAnnotations,
    onImportFile: importAuthoringAnnotationsFromFile,
    onSwitchLayer: switchAuthoringLayer,
    onAddLayer: addAuthoringLayer,
    onEditSnippet: startEditingSavedSnippet,
    onSaveSnippetEdit: saveSavedSnippetEdit,
    onCancelSnippetEdit: cancelEditingSavedSnippet,
    onRemoveSnippet: removeSavedSnippet,
    onNavigateSnippet: navigateToSavedSnippet,
    onBandColorChange: updateSavedBandColor,
  })
  bindAIInsightPanelControls({
    onToggle: handleAIStoryInsightButtonClick,
    onClose: closeAIInsightPanel,
    onRefresh: runAIStoryInsightScan,
    onAction: (action, candidateId) => {
      if (action === 'show') showAIInsightCandidate(candidateId)
      if (action === 'accept') acceptAIInsightCandidate(candidateId)
      if (action === 'reject') rejectAIInsightCandidate(candidateId)
    },
    onDraftInput: updateAIInsightCandidateDraft,
  })
  if (mySvg) {
    mySvg.addEventListener('click', handleAuthoringSvgClick, true)
  }
  setAuthoringMode(false)
  setAIInsightPanelVisible(false)
  updateAIInsightPanel()
  renderHighlightBands()
}

// -----------------------------------------------------------------------------
// Event filter controls
// -----------------------------------------------------------------------------

// Dynamically generate event type filter checkboxes based on event data.
const videoEventTypeFilterDiv = document.getElementById('videoEventTypeFilters')
const eventTypeFilterDiv = document.getElementById('eventTypeFilters')

// Store pending filter selections even if the storyline graph is still loading.
function applyEventTypeFilterSelection(
  filterTypes = getCheckedEventTypeFilters()
) {
  activeEventTypeFilterTypes = Array.isArray(filterTypes)
    ? [...filterTypes]
    : null
  window._pendingEventTypeFilterTypes = activeEventTypeFilterTypes
  if (window._drawEventsFilter) {
    window._drawEventsFilter(activeEventTypeFilterTypes)
  }
}

window.applyEventTypeFilterSelection = applyEventTypeFilterSelection

d3Fetch.json(EVENT_DATA_PATH).then(data => {
  allEventData = normalizeEventData(data) // Store for filtering
  // Get unique event types, but exclude 'Moving_In'
  const eventTypes = Array.from(new Set(data.map(ev => ev.eventType)))
    .filter(type => type !== 'Moving_In')
    .sort()
  // Separate event types into video and non-video
  const videoTypes = eventTypes.filter(type => VIDEO_EVENT_TYPES.includes(type))
  const otherTypes = eventTypes.filter(
    type => !VIDEO_EVENT_TYPES.includes(type)
  )

  videoEventTypeFilterDiv.innerHTML = ''
  eventTypeFilterDiv.innerHTML = ''

  // Add style block for grid if not already present
  if (!document.getElementById('eventTypeFilterGridStyle')) {
    const style = document.createElement('style')
    style.id = 'eventTypeFilterGridStyle'
    style.innerHTML = `
      #videoEventTypeFilters, #eventTypeFilters {
        display: grid;
        grid-template-columns: repeat(4, minmax(0, 1fr));
        gap: 8px 16px;
        align-items: center;
        margin-bottom: 8px;
      }
      .event-type-filter-item {
        display: flex;
        align-items: center;
        gap: 4px;
        padding: 2px 0;
        min-width: 0;
        white-space: nowrap;
      }
      .event-type-filter-item img {
        filter: grayscale(1) brightness(1.1) contrast(1.1);
      }
    `
    document.head.appendChild(style)
  }

  // Render video event type checkboxes
  videoTypes.forEach((type, idx) => {
    const id = `videoEventType_${idx}`
    let iconHTML = ''
    if (type === 'Family Interaction') {
      // Inline SVG for Family Interaction: rounded rectangle with glow
      iconHTML = `
        <svg width="20" height="20" viewBox="0 0 20 20" style="vertical-align:middle;object-fit:contain;">
          <defs>
            <filter id="family-glow" x="-40%" y="-40%" width="180%" height="180%">
              <feGaussianBlur in="SourceGraphic" stdDeviation="2" result="blur"/>
              <feMerge>
                <feMergeNode in="blur"/>
                <feMergeNode in="SourceGraphic"/>
              </feMerge>
            </filter>
          </defs>
          <rect x="2" y="2" width="12" height="16" rx="4" ry="4" fill="#000" stroke="#000" stroke-width="1"/>
        </svg>
      `
    } else {
      // Otherwise, use Interaction_Events SVG
      let iconPath = ''
      if (EVENT_ICON_GENERAL_TYPES.includes(type)) {
        if (EVENT_ICON_PNG_TYPES.includes(type)) {
          iconPath = `../../src/image/Events_General/${type}.png`
        } else {
          iconPath = `../../src/image/Events_General/${type}.svg`
        }
      } else {
        iconPath = `../../src/image/Interaction_Events/${type}.svg`
      }
      iconHTML = `<img src="${iconPath}" alt="${type}" style="width:20px;height:20px;vertical-align:middle;object-fit:contain;">`
    }
    videoEventTypeFilterDiv.innerHTML += `
      <label class="event-type-filter-item" for="${id}">
        <input type="checkbox" id="${id}" name="eventTypes" value="${type}" checked>
        ${iconHTML}
        <span>${type}</span>
      </label>
    `
  })

  // Render other event type checkboxes
  otherTypes.forEach((type, idx) => {
    const id = `eventType_${idx}`
    let iconHTML = ''
    let iconPath = ''
    if (EVENT_ICON_GENERAL_TYPES.includes(type)) {
      if (EVENT_ICON_PNG_TYPES.includes(type)) {
        iconPath = `../../src/image/Events_General/${type}.png`
      } else {
        iconPath = `../../src/image/Events_General/${type}.svg`
      }
    } else {
      iconPath = `../../src/image/Interaction_Events/${type}.svg`
    }
    iconHTML = `<img src="${iconPath}" alt="${type}" style="width:20px;height:20px;vertical-align:middle;object-fit:contain;">`
    eventTypeFilterDiv.innerHTML += `
      <label class="event-type-filter-item" for="${id}">
        <input type="checkbox" id="${id}" name="eventTypes" value="${type}" checked>
        ${iconHTML}
        <span>${type}</span>
      </label>
    `
  })

  // Add event listeners for both sections
  function handleFilterChange() {
    applyEventTypeFilterSelection()
  }
  document
    .querySelectorAll('input[name="eventTypes"]')
    .forEach(input => input.addEventListener('change', handleFilterChange))
})

// -----------------------------------------------------------------------------
// Base visualization setup and controls
// -----------------------------------------------------------------------------

// Initialise json files.
const jsonRead = d3Fetch.json(PARTICIPANTS_PATH) // Info of match
const jsonReadTwo = d3Fetch.json(EVENT_DATA_PATH)

// Screen Width and Height
const width = SVG_WIDTH
const height = SVG_HEIGHT

// We need to set the total timestamp first
// Sections decide how the interval of timeline display
let totalTimestamp
let lastTimestamp = null

// Canvas Origin
let xOrigin = 350,
  yOrigin = 60

let playerColour = { ...PLAYER_COLORS }

// Save location info for later use
let locationSet

let mySvg = $('#mySvg')[0]

let pt = mySvg.createSVGPoint()

const svg = Snap('#mySvg')
svg.attr({
  viewBox: `${SVG_INITIAL_VIEW_BOX.x} ${SVG_INITIAL_VIEW_BOX.y} ${SVG_INITIAL_VIEW_BOX.w} ${SVG_INITIAL_VIEW_BOX.h}`,
})

// Zoom/pan state is shared by buttons, wheel/pinch handlers, hand mode, and
// snippet/AI-band navigation.
let viewBox = { ...SVG_INITIAL_VIEW_BOX }
let minZoom = 0.1
let maxZoom = 10
let zoomStep = 1.1
let lastTouchDist = null
let isPanning = false
let panStart = { x: 0, y: 0 }
let panViewBoxStart = { x: 0, y: 0 }

function setViewBox(x, y, w, h) {
  viewBox.x = x
  viewBox.y = y
  viewBox.w = w
  viewBox.h = h
  svg.attr({ viewBox: `${x} ${y} ${w} ${h}` })
}

function zoomAt(cx, cy, scale) {
  // cx, cy: client coordinates
  // scale: zoom factor (>1 zoom in, <1 zoom out)
  // Convert client to SVG coordinates
  let pt = mySvg.createSVGPoint()
  pt.x = cx
  pt.y = cy
  let svgPt = pt.matrixTransform(mySvg.getScreenCTM().inverse())
  // Calculate new viewBox
  let newW = viewBox.w / scale
  let newH = viewBox.h / scale
  // Clamp zoom
  let zoomLevel = 14000 / newW
  if (zoomLevel < minZoom) {
    newW = 14000 / minZoom
    newH = 1200 / minZoom
  }
  if (zoomLevel > maxZoom) {
    newW = 14000 / maxZoom
    newH = 1200 / maxZoom
  }
  // Keep svgPt at same position in new viewBox
  let newX = svgPt.x - ((svgPt.x - viewBox.x) / viewBox.w) * newW
  let newY = svgPt.y - ((svgPt.y - viewBox.y) / viewBox.h) * newH
  setViewBox(newX, newY, newW, newH)
}

function resetZoom() {
  setViewBox(
    SVG_INITIAL_VIEW_BOX.x,
    SVG_INITIAL_VIEW_BOX.y,
    SVG_INITIAL_VIEW_BOX.w,
    SVG_INITIAL_VIEW_BOX.h
  )
}

// Mouse wheel and touchpad pinch zoom
mySvg.addEventListener(
  'wheel',
  function(e) {
    // Only zoom if ctrlKey (touchpad pinch) or if not horizontal scroll
    if (e.ctrlKey || Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
      e.preventDefault()
      let scale = e.deltaY < 0 ? zoomStep : 1 / zoomStep
      zoomAt(e.clientX, e.clientY, scale)
    }
  },
  { passive: false }
)

// Touch pinch zoom
mySvg.addEventListener('touchstart', function(e) {
  if (e.touches.length === 2) {
    lastTouchDist = Math.hypot(
      e.touches[0].clientX - e.touches[1].clientX,
      e.touches[0].clientY - e.touches[1].clientY
    )
  } else if (e.touches.length === 1) {
    isPanning = true
    panStart.x = e.touches[0].clientX
    panStart.y = e.touches[0].clientY
    panViewBoxStart.x = viewBox.x
    panViewBoxStart.y = viewBox.y
  }
})

mySvg.addEventListener(
  'touchmove',
  function(e) {
    if (e.touches.length === 2) {
      e.preventDefault()
      let newDist = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      )
      if (lastTouchDist) {
        let scale = newDist / lastTouchDist
        zoomAt(
          (e.touches[0].clientX + e.touches[1].clientX) / 2,
          (e.touches[0].clientY + e.touches[1].clientY) / 2,
          scale
        )
      }
      lastTouchDist = newDist
    } else if (e.touches.length === 1 && isPanning) {
      e.preventDefault()
      let dx = e.touches[0].clientX - panStart.x
      let dy = e.touches[0].clientY - panStart.y
      let scaleX = viewBox.w / mySvg.clientWidth
      let scaleY = viewBox.h / mySvg.clientHeight
      setViewBox(
        panViewBoxStart.x - dx * scaleX,
        panViewBoxStart.y - dy * scaleY,
        viewBox.w,
        viewBox.h
      )
    }
  },
  { passive: false }
)

mySvg.addEventListener('touchend', function(e) {
  if (e.touches.length < 2) {
    lastTouchDist = null
  }
  if (e.touches.length === 0) {
    isPanning = false
  }
})

/* --- Mouse drag pan with hand/cursor toggle --- */
let isMousePanning = false
let mousePanStart = { x: 0, y: 0 }
let mousePanViewBoxStart = { x: 0, y: 0 }
initAuthoringTool()

mySvg.addEventListener('mousedown', function(e) {
  // Only allow pan if hand mode is ON
  if (window._panMode && e.button === 0) {
    isMousePanning = true
    mousePanStart.x = e.clientX
    mousePanStart.y = e.clientY
    mousePanViewBoxStart.x = viewBox.x
    mousePanViewBoxStart.y = viewBox.y
    mySvg.style.cursor = 'grabbing'
    e.preventDefault()
  }
})
window.addEventListener('mousemove', function(e) {
  if (isMousePanning) {
    let dx = e.clientX - mousePanStart.x
    let dy = e.clientY - mousePanStart.y
    let scaleX = viewBox.w / mySvg.clientWidth
    let scaleY = viewBox.h / mySvg.clientHeight
    setViewBox(
      mousePanViewBoxStart.x - dx * scaleX,
      mousePanViewBoxStart.y - dy * scaleY,
      viewBox.w,
      viewBox.h
    )
  }
})
window.addEventListener('mouseup', function(e) {
  if (isMousePanning) {
    isMousePanning = false
    // Restore hand cursor if still in hand mode
    if (window._panMode) {
      mySvg.style.cursor = 'grab'
    } else {
      mySvg.style.cursor = 'default'
    }
  }
})

/* --- Expose zoom and reset for buttons --- */
window.resetZoom = resetZoom
window.zoomByButton = function(scale) {
  // Zoom at centre of SVG viewport
  const rect = mySvg.getBoundingClientRect()
  const cx = rect.left + rect.width / 2
  const cy = rect.top + rect.height / 2
  zoomAt(cx, cy, scale)
}

const onboardingGuide = initOnboardingGuide()
initStoryModal(onboardingGuide)
initZoomPanControls({
  resetZoom,
  zoomByButton: window.zoomByButton,
})
initEventFilterActionButtons({
  applySelection: applyEventTypeFilterSelection,
})

// Load the iStoryline graph, draw character lines, then attach Sims event data
// and authoring overlays.
async function main(fileName) {
  const iStorylineInstance = new iStoryline()
  const fileUrl = `../../data/${fileName.split('.')[1]}/${fileName}`
  let graph = await iStorylineInstance.loadFile(fileUrl)
  currentGraph = graph
  let dbSCANData
  // console.log(graph)

  // Read Json through the Promise and save participants data

  let participantsInfoData = await jsonRead.then(function(result) {
    return result['info']['participants']
  })

  let participantsInfo = []

  for (const element of participantsInfoData) {
    participantsInfo.push(element['participantId'], element['championName'])
  }

  const storyEventsData = await jsonReadTwo.catch(() => [])
  const deathEvent = Array.isArray(storyEventsData)
    ? storyEventsData.find(ev => ev.eventType === 'Death')
    : null
  const interactorNameWithDeath =
    deathEvent && deathEvent.interactor ? deathEvent.interactor : null

  // Build name->PlayerX map
  let nameToPlayer = {}
  for (let i = 0; i < participantsInfo.length; i += 2) {
    const pid = participantsInfo[i]
    const name = participantsInfo[i + 1]
    nameToPlayer[name] = 'Player' + pid
  }
  // Set playerWithDeathEvent for use in drawStoryline
  window.playerWithDeathEvent = nameToPlayer[interactorNameWithDeath] || null

  // Scale to window size
  const containerDom = document.getElementById('mySvg')
  const windowW = containerDom.clientWidth - 20
  const windowH = containerDom.clientHeight - 20
  // graph = iStorylineInstance.scale(80, 10, windowW / 1.2 , windowH / 1.5)
  graph = iStorylineInstance.scale(xOrigin, yOrigin, width * 2, height)
  currentGraph = graph

  // logStoryInfo(iStorylineInstance._story)

  const session = iStorylineInstance._story._tableMap.get('session')._mat._data

  const characters = graph.characters

  locationSet = iStorylineInstance._story._locations

  const storylines = graph.storylines
  currentStorylines = storylines

  // Timestamp
  const timestamps = iStorylineInstance._story._timeStamps
  totalTimestamp = timestamps[timestamps.length - 1]

  console.log('Match Length: ' + timeStamp(totalTimestamp))

  // convert the last timestamp into minutes

  let min = Math.floor((totalTimestamp / 1000 / 60) << 0),
    sec = Math.floor((totalTimestamp / 1000) % 60)

  // console.log(min + ':' + sec)

  const perTimeStamp = totalTimestamp / 10 //divided by 10

  let perMin = Math.floor((perTimeStamp / 1000 / 60) << 0),
    perSec = Math.floor((perTimeStamp / 1000) % 60)

  // console.log('Per timestamp: ' + perMin + ':' + perSec)

  // timeline()

  storylines.forEach((storyline, idx) => {
    drawStoryline(
      characters[idx],
      storyline,
      session,
      locationSet,
      perTimeStamp,
      participantsInfo
    )
  })

  // Provide a global function for filter UI to call
  window._drawEventsFilter = function(filterTypes) {
    activeEventTypeFilterTypes = Array.isArray(filterTypes)
      ? [...filterTypes]
      : null
    window._pendingEventTypeFilterTypes = activeEventTypeFilterTypes
    drawEvents(graph, participantsInfo, activeEventTypeFilterTypes)
  }

  // Initial draw, preserving any filter choice made before the visualization is ready.
  await drawEvents(
    graph,
    participantsInfo,
    window._pendingEventTypeFilterTypes || activeEventTypeFilterTypes
  )

  // --- Draw a cross at the last icon of the character who died ---
  // Find the Death event interactor name (already extracted above as interactorNameWithDeath)
  if (interactorNameWithDeath && nameToPlayer[interactorNameWithDeath]) {
    const playerId = nameToPlayer[interactorNameWithDeath]
    // Find the storyline index for this player
    const idx = graph.characters.findIndex(c => c === playerId)
    if (idx !== -1) {
      const storyline = graph.storylines[idx]
      // Only draw if storyline has more than 1 point (not at the first icon)
      if (storyline.length > 1) {
        // Get the last point (end of storyline)
        const lastPoint = getStorylinePoint(storyline[storyline.length - 1])
        // Get the position
        const x = lastPoint.x
        const y = lastPoint.y
        // Draw a cross (X) at this position, sized to icon
        const crossSize = 44 // slightly larger than icon
        const crossStroke = 6
        if (Number.isFinite(x) && Number.isFinite(y)) {
          svg
            .line(
              x - crossSize / 2,
              y - crossSize / 2,
              x + crossSize / 2,
              y + crossSize / 2
            )
            .attr({
              stroke: playerColour[playerId] || '#000',
              'stroke-width': crossStroke,
              'stroke-linecap': 'round',
              opacity: 0.95,
            })
          svg
            .line(
              x - crossSize / 2,
              y + crossSize / 2,
              x + crossSize / 2,
              y - crossSize / 2
            )
            .attr({
              stroke: playerColour[playerId] || '#000',
              'stroke-width': crossStroke,
              'stroke-linecap': 'round',
              opacity: 0.95,
            })
        }
      }
    }
  }

  // await timelineX(graph)

  $('#tip').remove()

  return iStorylineInstance
}

function timeStamp(perTimestamp) {
  let perMin = Math.floor((perTimestamp / 1000 / 60) << 0)
  return perMin + ' Min'
}

// Draw event icons and connectors for the current filter state. The draw version
// prevents slower async SVG loads from repainting after a newer filter redraw.
async function drawEvents(graph, participantsInfo, filterTypes = null) {
  const drawVersion = ++eventDrawVersion
  const requestedFilterTypes = Array.isArray(filterTypes)
    ? [...filterTypes]
    : null
  // --- Improved: Use a dedicated SVG group for all event icons/lines ---
  // Create or select a single group for all event elements
  let eventsGroup = svg.select('#events-group')
  if (!eventsGroup) {
    eventsGroup = svg.group().attr({ id: 'events-group' })
    // Ensure it's on top
    svg.append(eventsGroup)
  } else {
    // Remove all children (icons, lines, etc)
    while (eventsGroup.node.firstChild) {
      eventsGroup.node.removeChild(eventsGroup.node.firstChild)
    }
  }

  // Remove any old event-icon-group and mock-event-dotted-line elements outside the group (legacy)
  svg.selectAll('.event-icon-group').forEach(function(g) {
    g.remove()
  })
  svg.selectAll('.mock-event-dotted-line').forEach(function(g) {
    g.remove()
  })
  clearEventVisualRegistry()
  renderHighlightBands()

  // Use allEventData if available, otherwise fallback to jsonReadTwo
  let dataPromise = allEventData.length
    ? Promise.resolve(allEventData)
    : jsonReadTwo

  await dataPromise.then(function(result) {
    if (drawVersion !== eventDrawVersion) return
    const data = normalizeEventData(result)

    renderMovingInEvents({
      events: data,
      svg,
      Snap,
      graph,
      mySvg,
      xOrigin,
      playerColour,
      calculateBorderLength,
      registerSelectableEventElement,
    })

    const showFamilyInteractionLines =
      !requestedFilterTypes ||
      requestedFilterTypes.includes('Family Interaction')
    const familyInteractionLineGroups = showFamilyInteractionLines
      ? collectFamilyInteractionLineGroups(data, graph)
      : []

    for (let i in data) {
      let eventType = data[i]['eventType']
      const rendererKind = getEventRendererKind(eventType)
      if (requestedFilterTypes && !requestedFilterTypes.includes(eventType))
        continue // Filter out unwanted types

      // let posX = data[i]['position']['x']
      // let posY = data[i]['position']['y']
      let interactee = data[i]['interactee']
      let interactor = data[i]['interactor']

      let border

      let interacteeText, interactorText

      let interacteeIcon, interactorIcon

      let interactorBorder

      let eventInfo

      let interacteeNameElement, interactorNameElement

      let videoIcon

      // General visualizations with icons and tooltips + video
      // deleted eventType === 'Relocation'
      if (rendererKind === 'system-sim-status') {
        const iconSize = 30
        const offset = iconSize / 2

        let playerIndex = data[i]['interactorID']
        let currentTimestamp = data[i]['timestamp']
        let currentPlayer = 'Player' + String(playerIndex)
        let eventDetails = data[i]['eventDetails']
        let deathPosX = graph.getCharacterX(currentPlayer, currentTimestamp)
        let deathPosY = graph.getCharacterY(currentPlayer, currentTimestamp)

        const glowFilterId = ensureSvgGlowFilter(
          svg,
          document,
          'system-sim-status-glow-filter'
        )

        // Load the SVG so we can recolor it to the player's colour
        // Use Snap.load to fetch the SVG, clear existing fills and apply player colour to shapes
        Snap.load(
          `../../src/image/Interaction_Events/${eventType}.svg`,
          function(f) {
            try {
              // Remove explicit fills so we can override
              f.selectAll('*').forEach(function(el) {
                el.attr({ fill: null })
              })
            } catch (e) {
              // ignore if selection fails
            }

            // Apply player colour to common shape elements
            try {
              f.selectAll(
                'path, rect, circle, ellipse, polygon, polyline'
              ).forEach(function(el) {
                el.attr({
                  fill: playerColour[currentPlayer] || '#000',
                  stroke: '#000',
                  'fill-opacity': 0.9,
                })
              })
            } catch (e) {
              // ignore
            }

            // Create a group and insert the loaded SVG
            let g = eventsGroup.group()
            g.append(f)
            g.attr({
              transform: `translate(${deathPosX - offset},${deathPosY -
                offset})`,
              class: 'event-icon-group',
            })
            registerSelectableEventElement(g, data[i], {
              x: deathPosX,
              y: deathPosY,
              radius: 24,
            })

            // Ensure size
            g.selectAll('svg').forEach(function(svgEl) {
              svgEl.attr({ width: iconSize, height: iconSize })
            })

            // Tooltip logic (preserved and independent)
            let tooltipElements = {}
            g.hover(
              event => {
                // --- Add glow filter on hover ---
                g.attr({ filter: `url(#${glowFilterId})` })
                g.attr({
                  transform: `translate(${deathPosX},${deathPosY}) scale(1.5) translate(${-offset},${-offset})`,
                })

                pt.x = event.clientX
                pt.y = event.clientY
                pt = pt.matrixTransform(mySvg.getScreenCTM().inverse())
                let tipX = pt.x + 20
                let tipY = pt.y + 20
                if (tipY >= 950) tipY -= 70
                if (tipX >= 5700) tipX -= 220
                let length =
                  calculateBorderLength(eventDetails, 50) < 250
                    ? 250
                    : calculateBorderLength(eventDetails, 50)
                let tooltipWidth = Math.min(length, 600)
                let tooltipHeight = 180
                let border = svg
                  .rect(tipX, tipY, tooltipWidth, tooltipHeight, 10, 10)
                  .attr({
                    stroke: 'black',
                    fill: 'rgba(255,255,255, 0.9)',
                    strokeWidth: '3px',
                  })
                let interactorText = svg.text(
                  35 + tipX,
                  25 + tipY,
                  'Interactor: ' + interactor
                )
                let interactorIcon = svg.image(
                  `../../src/image/Characters/${interactor}.png`,
                  38 + tipX,
                  40 + tipY,
                  40,
                  40
                )
                // video icon at top-right of the border
                let videoIcon = svg.image(
                  `../../src/image/video.png`,
                  tipX + tooltipWidth - 38,
                  tipY + 12,
                  24,
                  24
                )
                // --- Use foreignObject for word-wrapping eventDetails ---
                let detailsForeign = document.createElementNS(
                  'http://www.w3.org/2000/svg',
                  'foreignObject'
                )
                detailsForeign.setAttribute('x', 35 + tipX)
                detailsForeign.setAttribute('y', 35 + 50 + 20 + tipY)
                detailsForeign.setAttribute('width', tooltipWidth - 40)
                detailsForeign.setAttribute('height', tooltipHeight - 80)
                let detailsDiv = document.createElement('div')
                detailsDiv.style.maxWidth = tooltipWidth - 40 + 'px'
                detailsDiv.style.wordBreak = 'break-word'
                detailsDiv.style.whiteSpace = 'pre-wrap'
                detailsDiv.style.fontSize = '18px'
                detailsDiv.style.fontFamily = 'inherit'
                detailsDiv.style.color = '#222'
                detailsDiv.textContent = eventDetails
                detailsForeign.appendChild(detailsDiv)
                mySvg.appendChild(detailsForeign)
                let interactorBorder = svg
                  .rect(35 + tipX, 37 + tipY, 46, 46)
                  .attr({
                    fill: 'none',
                    stroke: `${playerColour[currentPlayer]}`,
                    'stroke-width': '3',
                    opacity: 0.7,
                  })
                tooltipElements = {
                  border,
                  interactorText,
                  interactorIcon,
                  detailsForeign,
                  interactorBorder,
                  videoIcon,
                }
              },
              () => {
                g.attr({ filter: null })
                g.attr({
                  transform: `translate(${deathPosX - offset},${deathPosY -
                    offset})`,
                })
                Object.values(tooltipElements).forEach(el => {
                  if (el)
                    el.remove && typeof el.remove === 'function'
                      ? el.remove()
                      : el.parentNode && el.parentNode.removeChild(el)
                })
                tooltipElements = {}
              }
            )

            g.click(function(event) {
              showVideoEventModal({
                documentRef: document,
                videoPath: VIDEO_PATH,
                event: data[i],
                getVideoClipRangeFromEvent,
                overlayId: 'system-sim-status-modal-overlay',
                videoId: 'system-sim-status-modal-video',
              })
            })
          }
        )
      }

      if (rendererKind === 'standard-interaction') {
        renderStandardInteractionEvent({
          event: data[i],
          eventsGroup,
          svg,
          Snap,
          graph,
          mySvg,
          documentRef: document,
          playerColour,
          calculateBorderLength,
          registerSelectableEventElement,
        })
      }

      // interactive events (colour-coded) with icons and tooltips (special case for Choice)
      if (isChoiceOrDeathEvent(eventType)) {
        const iconSize = 30
        const offset = iconSize / 2

        let playerIndex = data[i]['interactorID']
        let currentTimestamp = data[i]['timestamp']
        let currentPlayer = 'Player' + String(playerIndex)

        let eventDetails = data[i]['eventDetails']

        let deathPosX = graph.getCharacterX(currentPlayer, currentTimestamp)
        let deathPosY = graph.getCharacterY(currentPlayer, currentTimestamp)

        const glowFilterId = ensureSvgGlowFilter(
          svg,
          document,
          'choice-glow-filter'
        )

        // Use Snap.svg to load the SVG, set fill, then place at correct position
        Snap.load(
          `../../src/image/Interaction_Events/${eventType}.svg`,
          function(f) {
            // Remove all fill attributes from the SVG to ensure full override
            f.selectAll('*').forEach(function(el) {
              el.attr({ fill: null })
            })
            // Set fill for all paths/shapes in the SVG
            f.selectAll(
              'path, rect, circle, ellipse, polygon, polyline'
            ).forEach(function(el) {
              el.attr({ fill: playerColour[currentPlayer] || '#000' })
              el.attr({ stroke: '#000' })
            })
            // Create a group for the icon
            let g = eventsGroup.group()
            g.append(f)
            // Set the icon to the correct size and position
            g.transform('')
            g.attr({
              transform: `translate(${deathPosX - offset},${deathPosY -
                offset})`,
              class: 'event-icon-group',
            })
            registerSelectableEventElement(g, data[i], {
              x: deathPosX,
              y: deathPosY,
              radius: 24,
            })
            // Set the bounding box to the correct size
            g.selectAll('svg').forEach(function(svgEl) {
              svgEl.attr({ width: iconSize, height: iconSize })
            })
            // Add hover behaviour as before, with glow effect
            g.hover(
              event => {
                // --- Add glow filter on hover ---
                g.attr({ filter: `url(#${glowFilterId})` })

                // --- Immediately scale icon to 150% (no animation) about its center ---
                // Move origin to center, scale, then move origin back so the center remains at deathPosX,deathPosY
                g.attr({
                  transform: `translate(${deathPosX},${deathPosY}) scale(1.5) translate(${-offset},${-offset})`,
                })

                pt.x = event.clientX
                pt.y = event.clientY

                pt = pt.matrixTransform(mySvg.getScreenCTM().inverse())

                // Offset tooltip so it does not appear under the cursor
                let tipX = pt.x + 20
                let tipY = pt.y + 20

                if (tipY >= 950) {
                  tipY -= 70
                }

                if (tipX >= 5700) {
                  tipX -= 220
                }

                currentPlayer = 'Player' + String(playerIndex)

                // --- Consistent tooltip width, height, and word-wrapping for Choice event ---
                let length
                if (calculateBorderLength(eventDetails, 50) < 250) {
                  length = 250
                } else {
                  length = calculateBorderLength(eventDetails, 50)
                }
                // Limit tooltip width to 600px max
                let tooltipWidth = Math.min(length, 600)
                // Calculate tooltip height considering all elements (like other events)
                const padding = 0.02
                const labelHeight = 28 // "Interactor:" and "Interactee:" text
                const iconHeight = 40 // icon
                const iconMargin = 15 // margin between text and border
                const borderMargin = 20 // margin between icon and border
                const nameHeight = 28 // interactee/interactor name text
                const nameMargin = 10 // margin between icon and name
                const detailsHeight = 60 // eventDetails (foreignObject)
                // Total: 2 label lines, 2 icons, 2 name lines, margins, details
                let tooltipContentHeight =
                  // Top labels
                  labelHeight +
                  // Icon row
                  iconMargin +
                  iconHeight +
                  // Name row
                  nameMargin +
                  nameHeight +
                  // Details row
                  borderMargin +
                  detailsHeight +
                  // Bottom margin
                  borderMargin
                let tooltipHeight = tooltipContentHeight * (1 + 2 * padding)
                border = svg
                  .rect(tipX, tipY, tooltipWidth, tooltipHeight, 10, 10)
                  .attr({
                    stroke: 'black',
                    fill: 'rgba(255,255,255, 0.9)',
                    strokeWidth: '3px',
                  })

                interacteeText = svg.text(130 + tipX, 25 + tipY, 'Interactee: ')
                interactorText = svg.text(35 + tipX, 25 + tipY, 'Interactor: ')

                interacteeIcon = svg.image(
                  `../../src/image/Characters/${interactee}.png`,
                  133 + tipX,
                  41 + tipY,
                  40,
                  40
                )

                interactorIcon = svg.image(
                  `../../src/image/Characters/${interactor}.png`,
                  38 + tipX,
                  40 + tipY,
                  40,
                  40
                )

                // Add coloured borders for interactor and interactee (like Relocation)
                let interactorBorder = svg
                  .rect(38 + tipX, 40 + tipY, 40, 40)
                  .attr({
                    fill: 'none',
                    stroke: playerColour[currentPlayer] || '#000',
                    'stroke-width': '3',
                    opacity: 0.7,
                  })
                // Try to get interactee player colour
                let interacteePlayerIndex = data[i]['interacteeID']
                let interacteePlayer = 'Player' + String(interacteePlayerIndex)
                let interacteeBorder = svg
                  .rect(133 + tipX, 41 + tipY, 40, 40)
                  .attr({
                    fill: 'none',
                    stroke: playerColour[interacteePlayer] || '#000',
                    'stroke-width': '3',
                    opacity: 0.7,
                  })

                interacteeNameElement = svg.text(
                  130 + tipX,
                  35 + 50 + 20 + tipY,
                  interactee
                )

                interactorNameElement = svg.text(
                  35 + tipX,
                  35 + 50 + 20 + tipY,
                  interactor
                )

                videoIcon = svg.image(
                  `../../src/image/video.png`,
                  tipX + tooltipWidth - 38,
                  tipY + 12,
                  24,
                  24
                )

                // Use foreignObject for word-wrapping eventDetails
                let foreign = document.createElementNS(
                  'http://www.w3.org/2000/svg',
                  'foreignObject'
                )
                foreign.setAttribute('x', 35 + tipX)
                foreign.setAttribute('y', 35 + 50 + 20 + tipY + 45)
                foreign.setAttribute('width', 530)
                foreign.setAttribute('height', 60)
                let div = document.createElement('div')
                div.style.maxWidth = tooltipWidth - 40 + 'px'
                div.style.wordBreak = 'break-word'
                div.style.whiteSpace = 'pre-wrap'
                div.style.fontSize = '18px'
                div.style.fontFamily = 'inherit'
                div.style.color = '#222'
                div.textContent = eventDetails
                foreign.appendChild(div)
                mySvg.appendChild(foreign)
                eventInfo = {
                  remove: () => foreign.remove(),
                }
              },
              () => {
                // --- Remove glow filter on mouseleave ---
                g.attr({ filter: null })

                // --- Restore original icon size/transform immediately (no animation) ---
                g.attr({
                  transform: `translate(${deathPosX - offset},${deathPosY -
                    offset})`,
                })
                videoIcon.remove()
                border.remove()
                interacteeText.remove()
                interacteeIcon.remove()
                interactorText.remove()
                interactorIcon.remove()
                // Remove the new borders if present
                let allRects = svg.selectAll('rect')
                if (allRects && allRects.length) {
                  // Remove only the last two (the borders we just added)
                  allRects[allRects.length - 1].remove()
                  allRects[allRects.length - 2].remove()
                }
                interacteeNameElement.remove()
                interactorNameElement.remove()
                eventInfo.remove()
              }
            )

            g.click(function(event) {
              showVideoEventModal({
                documentRef: document,
                videoPath: VIDEO_PATH,
                event: data[i],
                getVideoClipRangeFromEvent,
                overlayId: 'choice-modal-video',
                videoId: 'choice-modal-video',
              })
            })
          }
        )
      }

      // this contains the logic for connecting events with multiple participants
      if (rendererKind === 'family-interaction') {
        // Rectangle size
        const rectWidth = 15
        const rectHeight = 20

        let playerIndex = data[i]['interactorID']
        let currentTimestamp = data[i]['timestamp']
        let currentPlayer = 'Player' + String(playerIndex)
        let eventDetails = data[i]['eventDetails']

        // Always treat interactee/interacteeID as arrays
        let interacteeArr = Array.isArray(data[i]['interacteeID'])
          ? data[i]['interacteeID']
          : [data[i]['interacteeID']]
        let interacteeNameArr = Array.isArray(data[i]['interactee'])
          ? data[i]['interactee']
          : [data[i]['interactee']]
        // Collect all participants: interactor + interactees
        let allIDs = [data[i]['interactorID'], ...interacteeArr]
        let allNames = [data[i]['interactor'], ...interacteeNameArr]
        let allPlayers = allIDs.map(id => 'Player' + String(id))
        let allRects = []

        const glowFilterId = ensureSvgGlowFilter(
          svg,
          document,
          'family-interaction-glow-filter'
        )

        // Create a Snap group for all rectangles in this event
        let rectGroup = eventsGroup.group()
        rectGroup.attr({ class: 'family-interaction-rect-group' })

        for (let k = 0; k < allPlayers.length; k++) {
          let posX = graph.getCharacterX(allPlayers[k], currentTimestamp)
          let posY = graph.getCharacterY(allPlayers[k], currentTimestamp)
          // Draw the rectangle with solid player colour fill
          let rect = rectGroup
            .rect(
              posX - rectWidth / 2,
              posY - rectHeight / 2,
              rectWidth,
              rectHeight,
              8, // rx for rounded corners
              8 // ry for rounded corners
            )
            .attr({
              fill: playerColour[allPlayers[k]] || '#222',
              class: 'event-icon-group',
            })
          registerSelectableEventElement(rect, data[i], {
            x: posX,
            y: posY,
            radius: 22,
          })
          allRects.push(rect)
        }

        // Add hover for each rectangle, but effect applies to the whole group
        // We'll use the first rectangle as the "main" for tooltip logic
        for (let k = 0; k < allRects.length; k++) {
          let rect = allRects[k]
          rect.hover(
            (function() {
              // We need to keep references to the tooltip elements and icon/border elements
              let tooltipElements = {}
              let iconElements = []
              let iconBorders = []
              return function(event) {
                // --- Glow and scale effect for all rectangles in the group ---
                rectGroup.attr({ filter: `url(#${glowFilterId})` })
                // Scale about each rectangle's centre
                for (let m = 0; m < allRects.length; m++) {
                  let r = allRects[m]
                  let bbox = r.getBBox()
                  let cx = bbox.x + bbox.width / 2
                  let cy = bbox.y + bbox.height / 2
                  r.transform(`s1.5,1.5,${cx},${cy}`)
                }

                // Tooltip logic (show all participants for all rectangles in the group)
                pt.x = event.clientX
                pt.y = event.clientY
                pt = pt.matrixTransform(mySvg.getScreenCTM().inverse())
                let tipX = pt.x
                let tipY = pt.y
                if (pt.y >= 950) tipY -= 50
                if (pt.x >= 5700) tipX -= 200

                // --- Tooltip layout for Family Interaction event ---
                let length =
                  calculateBorderLength(eventDetails, 50) <= 370
                    ? 380
                    : calculateBorderLength(eventDetails, 50)
                // Limit tooltip width to 600px max
                let tooltipWidth = Math.min(length, 600)
                let tooltipHeight = 180
                let border = svg
                  .rect(tipX, tipY, tooltipWidth, tooltipHeight, 10, 10)
                  .attr({
                    stroke: 'black',
                    fill: 'rgba(255,255,255, 0.9)',
                    strokeWidth: '3px',
                  })

                // Show all participants' names in a single line
                let interactorText = svg.text(
                  35 + tipX,
                  25 + tipY,
                  'Character Involved: ' + allNames.join(', ')
                )

                videoIcon = svg.image(
                  `../../src/image/video.png`,
                  tipX + tooltipWidth - 38,
                  tipY + 12,
                  24,
                  24
                )

                // Show all participant icons in a row, with colour-coded borders
                let iconSize = 40
                let iconSpacing = 10
                let iconStartX = 38 + tipX
                let iconY = 40 + tipY
                iconBorders = []
                iconElements = []
                for (let p = 0; p < allNames.length; p++) {
                  let px = iconStartX + p * (iconSize + iconSpacing)
                  let playerName = allNames[p]
                  let playerId = allPlayers[p]
                  // Icon
                  let icon = svg.image(
                    `../../src/image/Characters/${playerName}.png`,
                    px,
                    iconY,
                    iconSize,
                    iconSize
                  )
                  iconElements.push(icon)
                  // Border
                  let borderRect = svg
                    .rect(px, iconY, iconSize, iconSize)
                    .attr({
                      fill: 'none',
                      stroke: playerColour[playerId] || '#222',
                      'stroke-width': '3',
                      opacity: 0.7,
                    })
                  iconBorders.push(borderRect)
                }

                // Draw eventDetails as a single line
                // Use foreignObject for word-wrapping eventDetails
                let foreign = document.createElementNS(
                  'http://www.w3.org/2000/svg',
                  'foreignObject'
                )
                foreign.setAttribute('x', 35 + tipX)
                foreign.setAttribute('y', 35 + 50 + 20 + tipY)
                foreign.setAttribute('width', 530)
                foreign.setAttribute('height', 80)
                let div = document.createElement('div')
                div.style.maxWidth = '530px'
                div.style.wordBreak = 'break-word'
                div.style.whiteSpace = 'pre-wrap'
                div.style.fontSize = '18px'
                div.style.fontFamily = 'inherit'
                div.style.color = '#222'
                div.textContent = eventDetails
                foreign.appendChild(div)
                mySvg.appendChild(foreign)
                let interactorNameElement = {
                  remove: () => foreign.remove(),
                }

                // Store references for cleanup
                tooltipElements = {
                  border,
                  interactorText,
                  interactorNameElement,
                  iconElements,
                  iconBorders,
                }

                // Attach to rect for access in mouseleave
                rect.data('family-tooltip-elements', tooltipElements)
              }
            })(),
            function() {
              // Remove the tooltip immediately on mouseleave
              let tooltipElements = rect.data('family-tooltip-elements')
              if (tooltipElements) {
                if (tooltipElements.border) tooltipElements.border.remove()
                if (tooltipElements.interactorText)
                  tooltipElements.interactorText.remove()
                if (tooltipElements.interactorNameElement)
                  tooltipElements.interactorNameElement.remove()
                if (
                  tooltipElements.iconElements &&
                  tooltipElements.iconElements.length
                ) {
                  tooltipElements.iconElements.forEach(el => el && el.remove())
                }
                if (
                  tooltipElements.iconBorders &&
                  tooltipElements.iconBorders.length
                ) {
                  tooltipElements.iconBorders.forEach(el => el && el.remove())
                }
              }
              // Remove glow and restore transform for all rectangles in the group
              rectGroup.attr({ filter: null })
              for (let m = 0; m < allRects.length; m++) {
                allRects[m].transform('')
              }
              // Remove the stored data
              rect.data('family-tooltip-elements', null)

              videoIcon.remove()
            }
          )

          rect.click(function(event) {
            showVideoEventModal({
              documentRef: document,
              videoPath: VIDEO_PATH,
              event: data[i],
              getVideoClipRangeFromEvent,
              overlayId: 'family-interaction-modal-video',
              videoId: 'family-interaction-modal-video',
            })
          })
        }
        // No need to draw lines here; handled above for all participants
      }

      // different size for death icon + video in tooltip
      // if (eventType === 'Death') {
      //   const iconSize = 50
      //   const offset = iconSize / 2
      //
      //   let playerIndex = data[i]['interactorID']
      //   let currentTimestamp = data[i]['timestamp']
      //   let currentPlayer = 'Player' + String(playerIndex)
      //
      //   let eventDetails = data[i]['eventDetails']
      //
      //   let deathPosX = graph.getCharacterX(currentPlayer, currentTimestamp)
      //   let deathPosY = graph.getCharacterY(currentPlayer, currentTimestamp)
      //
      //   // console.log(eventDetails, deathPosX, deathPosY)
      //
      //   eventsGroup
      //     .image(
      //       `../../src/image/Events_General/${eventType}.svg`,
      //       deathPosX - offset,
      //       deathPosY - offset,
      //       iconSize,
      //       iconSize
      //     )
      //     .attr({ class: 'event-icon-group' })
      //     .hover(
      //       event => {
      //         pt.x = event.clientX
      //         pt.y = event.clientY
      //
      //         pt = pt.matrixTransform(mySvg.getScreenCTM().inverse())
      //
      //         // Offset tooltip so it does not appear under the cursor
      //         let tipX = pt.x + 20
      //         let tipY = pt.y + 20
      //
      //         if (tipY >= 950) {
      //           tipY -= 70
      //         }
      //
      //         if (tipX >= 5700) {
      //           tipX -= 220
      //         }
      //
      //         currentPlayer = 'Player' + String(playerIndex)
      //
      //         let length
      //
      //         if (calculateBorderLength(eventDetails, 50) < 250) {
      //           length = 250
      //         } else {
      //           length = calculateBorderLength(eventDetails, 50)
      //         }
      //
      //         // backup arg with minimap - tipX, tipY, 250, 325, 10, 10
      //         // Limit tooltip width to 600px max
      //         let tooltipWidth = Math.min(length, 600)
      //         border = svg.rect(tipX, tipY, tooltipWidth, 400, 10, 10).attr({
      //           stroke: 'black',
      //           fill: 'rgba(255,255,255, 0.9)',
      //           strokeWidth: '3px',
      //         })
      //
      //         // interacteeText = svg.text(130 + tipX, 25 + tipY, 'Interactee: ')
      //         interactorText = svg.text(
      //           35 + tipX,
      //           25 + tipY,
      //           'Interactor: ' + interactor
      //         )
      //
      //         interactorIcon = svg.image(
      //           `../../src/image/Characters/${interactor}.png`,
      //           38 + tipX,
      //           40 + tipY,
      //           40,
      //           40
      //         )
      //
      //         interactorBorder = svg.rect(35 + tipX, 37 + tipY, 46, 46).attr({
      //           fill: 'none',
      //           stroke: `${playerColour[currentPlayer]}`,
      //           'stroke-width': '3',
      //           opacity: 0.7,
      //         })
      //
      //         // Remove any existing wrapper before creating a new one
      //         let oldWrapper = document.getElementById(
      //           'relocation-tooltip-wrapper'
      //         )
      //         if (oldWrapper) {
      //           let oldVideo = oldWrapper.querySelector(
      //             '#relocation-tooltip-video'
      //           )
      //           if (oldVideo) oldVideo.pause()
      //           oldWrapper.remove()
      //         }
      //
      //         // Add video element to tooltip, positioned and scaled to match SVG border
      //         // Remove any existing wrapper before creating a new one
      //         let wrapper = document.createElement('div')
      //         wrapper.style.position = 'fixed'
      //         wrapper.setAttribute('id', 'relocation-tooltip-wrapper')
      //         wrapper.style.zIndex = 1000
      //         wrapper.style.pointerEvents = 'none'
      //         wrapper.style.overflow = 'hidden'
      //         wrapper.style.scrollbarWidth = 'none'
      //         wrapper.style.msOverflowStyle = 'none'
      //
      //         // Get the SVG border's bounding box in SVG coordinates
      //         let borderBBox = border.node.getBBox()
      //         // Convert SVG coordinates to screen coordinates
      //         let ctm = mySvg.getScreenCTM()
      //         // Top-left corner
      //         let svgX = borderBBox.x
      //         let svgY = borderBBox.y
      //         let svgW = borderBBox.width
      //         let svgH = borderBBox.height
      //         // Transform to screen coordinates
      //         let topLeft = mySvg.createSVGPoint()
      //         topLeft.x = svgX
      //         topLeft.y = svgY
      //         topLeft = topLeft.matrixTransform(ctm)
      //         // Bottom-right corner
      //         let bottomRight = mySvg.createSVGPoint()
      //         bottomRight.x = svgX + svgW
      //         bottomRight.y = svgY + svgH
      //         bottomRight = bottomRight.matrixTransform(ctm)
      //         // Calculate width/height in screen space
      //         let screenW = bottomRight.x - topLeft.x
      //         let screenH = bottomRight.y - topLeft.y
      //         // Set wrapper position and size
      //         wrapper.style.left = `${topLeft.x}px`
      //         wrapper.style.top = `${topLeft.y}px`
      //         wrapper.style.width = `${screenW}px`
      //         wrapper.style.height = `${screenH}px`
      //
      //         // Calculate video size and position (2% padding on each side)
      //         let videoW = screenW * 0.96
      //         let videoH = screenH * 0.96
      //         let videoLeft = screenW * 0.2
      //         let videoTop = screenH * 0.35
      //
      //         let video = document.createElement('video')
      //         video.src = '../../src/video/sims4.mp4'
      //         video.controls = true
      //         video.autoplay = true
      //         video.loop = true
      //         video.muted = true
      //         video.setAttribute('id', 'relocation-tooltip-video')
      //         video.style.pointerEvents = 'auto'
      //         video.style.background = 'black'
      //         video.style.position = 'absolute'
      //         video.style.left = `${videoLeft}px`
      //         video.style.top = `${videoTop}px`
      //         video.style.width = `${videoW*0.6}px`
      //         video.style.height = `${videoH*0.6}px`
      //         video.style.borderRadius = '8px'
      //
      //         // Clip video to play only within the calculated range and loop
      //         const videoRange = getVideoClipRangeFromEvent(data[i], 5); // use the current event object
      //         video.addEventListener('loadedmetadata', function() {
      //           // Clamp end to video duration if needed
      //           const end = Math.min(videoRange.end, video.duration);
      //           video.currentTime = videoRange.start;
      //           video._clipStart = videoRange.start;
      //           video._clipEnd = end;
      //           const playPromise = video.play();
      //           if (playPromise !== undefined) {
      //             playPromise.catch(e => {
      //               console.warn('Autoplay play() failed:', e);
      //             });
      //           }
      //         });
      //         video.addEventListener('timeupdate', function() {
      //           // Loop only within the clip range
      //           if (video.currentTime < video._clipStart || video.currentTime >= video._clipEnd) {
      //             video.currentTime = video._clipStart;
      //             video.play();
      //           }
      //         });
      //
      //         wrapper.appendChild(video)
      //         document.body.appendChild(wrapper)
      //
      //         // Draw eventDetails as a single line
      //         // Use foreignObject for word-wrapping eventDetails
      //         let foreign = document.createElementNS('http://www.w3.org/2000/svg', 'foreignObject')
      //         foreign.setAttribute('x', 35 + tipX)
      //         foreign.setAttribute('y', 35 + 50 + 20 + tipY)
      //         foreign.setAttribute('width', 530)
      //         foreign.setAttribute('height', 320)
      //         let div = document.createElement('div')
      //         div.style.maxWidth = '530px'
      //         div.style.wordBreak = 'break-word'
      //         div.style.whiteSpace = 'pre-wrap'
      //         div.style.fontSize = '18px'
      //         div.style.fontFamily = 'inherit'
      //         div.style.color = '#222'
      //         div.textContent = eventDetails
      //         foreign.appendChild(div)
      //         mySvg.appendChild(foreign)
      //         interactorNameElement = {
      //           remove: () => foreign.remove()
      //         }
      //       },
      //       () => {
      //         border.remove()
      //         interactorText.remove()
      //         interactorIcon.remove()
      //         interactorNameElement.remove()
      //         interactorBorder.remove()
      //         // Remove the video wrapper if it exists
      //         let wrapper = document.getElementById(
      //           'relocation-tooltip-wrapper'
      //         )
      //         if (wrapper) {
      //           let video = wrapper.querySelector('#relocation-tooltip-video')
      //           if (video) {
      //             video.pause()
      //           }
      //           wrapper.remove()
      //         }
      //       }
      //     )
      // }

      // different style for letter icon: glow on hover, modal "letter" on click
      if (eventType === 'Letter') {
        const iconSize = 40
        const offset = iconSize / 2

        let playerIndex = data[i]['interactorID']
        let currentTimestamp = data[i]['timestamp']
        let currentPlayer = 'Player' + String(playerIndex)

        let eventDetails = data[i]['eventDetails']

        let deathPosX = graph.getCharacterX(currentPlayer, currentTimestamp)
        let deathPosY = graph.getCharacterY(currentPlayer, currentTimestamp)

        const glowFilterId = ensureSvgGlowFilter(
          svg,
          document,
          'letter-glow-filter'
        )

        // Use Snap.svg to load the SVG, set fill, then place at correct position
        Snap.load(
          `../../src/image/Interaction_Events/${eventType}.svg`,
          function(f) {
            // Remove all fill attributes from the SVG to ensure full override
            f.selectAll('*').forEach(function(el) {
              el.attr({ fill: null })
            })
            // Set fill for all paths/shapes in the SVG
            f.selectAll(
              'path, rect, circle, ellipse, polygon, polyline'
            ).forEach(function(el) {
              el.attr({
                fill: playerColour[currentPlayer] || '#000',
                'fill-opacity': 0.7,
                stoke: '#000',
                'stroke-opacity': 1.0,
              })
            })
            // Create a group for the icon
            let g = eventsGroup.group()
            g.append(f)
            // Set the icon to the correct size and position
            g.transform('')
            g.attr({
              transform: `translate(${deathPosX - offset},${deathPosY -
                offset})`,
              class: 'event-icon-group',
            })
            registerSelectableEventElement(g, data[i], {
              x: deathPosX,
              y: deathPosY,
              radius: 28,
            })
            // Set the bounding box to the correct size
            g.selectAll('svg').forEach(function(svgEl) {
              svgEl.attr({ width: iconSize, height: iconSize })
            })
            // Add glow on hover, remove on mouseleave
            g.hover(
              () => {
                g.attr({ filter: `url(#${glowFilterId})` })
              },
              () => {
                g.attr({ filter: null })
              }
            )
            // On click, show modal "letter" layout
            g.click(function(event) {
              // Remove any existing overlay
              let oldOverlay = document.getElementById('letter-modal-overlay')
              if (oldOverlay) {
                oldOverlay.remove()
              }

              // Create overlay
              let overlay = document.createElement('div')
              overlay.setAttribute('id', 'letter-modal-overlay')
              overlay.style.position = 'fixed'
              overlay.style.left = '0'
              overlay.style.top = '0'
              overlay.style.width = '100vw'
              overlay.style.height = '100vh'
              overlay.style.background = 'rgba(80,80,80,0.7)'
              overlay.style.zIndex = 2000
              overlay.style.display = 'flex'
              overlay.style.alignItems = 'center'
              overlay.style.justifyContent = 'center'

              // Modal content container styled as a letter
              let modal = document.createElement('div')
              modal.style.position = 'relative'
              modal.style.background =
                'linear-gradient(135deg, #fffbe6 90%, #f7ecd0 100%)'
              modal.style.borderRadius = '18px'
              modal.style.boxShadow = '0 4px 32px rgba(0,0,0,0.18)'
              modal.style.padding = '48px 36px 36px 36px'
              modal.style.display = 'flex'
              modal.style.flexDirection = 'column'
              modal.style.alignItems = 'center'
              modal.style.justifyContent = 'flex-start'
              modal.style.width = 'min(90vw, 520px)'
              modal.style.maxWidth = '95vw'
              modal.style.minWidth = '320px'
              modal.style.minHeight = '220px'
              modal.style.border = '2px solid #e2c48d'
              modal.style.fontFamily = '"Georgia", "Times New Roman", serif'
              modal.style.position = 'relative'
              modal.style.overflow = 'visible'

              // Add a "fold" effect at the top
              let fold = document.createElement('div')
              fold.style.position = 'absolute'
              fold.style.top = '0'
              fold.style.left = '0'
              fold.style.width = '100%'
              fold.style.height = '32px'
              fold.style.background =
                'linear-gradient(180deg, #f7ecd0 60%, #fffbe6 100%)'
              fold.style.borderTopLeftRadius = '18px'
              fold.style.borderTopRightRadius = '18px'
              fold.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)'
              fold.style.zIndex = 1
              modal.appendChild(fold)

              // "Last Will Letter" heading
              let heading = document.createElement('div')
              heading.innerText =
                'THE LAST WILL AND TESTAMENT OF THE FAMILY' + "'" + 'S ESTATE'
              heading.style.fontWeight = 'bold'
              heading.style.fontSize = '2.1rem'
              heading.style.letterSpacing = '0.08em'
              heading.style.marginBottom = '4px'
              heading.style.marginTop = '18px'
              heading.style.color = '#b08d57'
              heading.style.textShadow = '0 1px 0 #fffbe6'
              heading.style.zIndex = 2
              heading.style.width = '100%'
              heading.style.textAlign = 'center'
              heading.style.display = 'block'
              modal.appendChild(heading)

              // Letter content (immediately after title)
              let letterContent = document.createElement('div')
              letterContent.innerText = eventDetails
              letterContent.style.fontSize = '1.25rem'
              letterContent.style.lineHeight = '1.7'
              letterContent.style.color = '#5a4a2c'
              letterContent.style.background = 'rgba(255,255,255,0.7)'
              letterContent.style.padding = '18px 16px'
              letterContent.style.borderRadius = '10px'
              letterContent.style.boxShadow = '0 1px 4px rgba(200,180,120,0.08)'
              letterContent.style.marginBottom = '24px'
              letterContent.style.marginTop = '0'
              letterContent.style.whiteSpace = 'pre-wrap'
              letterContent.style.wordBreak = 'break-word'
              letterContent.style.zIndex = 2
              letterContent.style.width = '100%'
              letterContent.style.display = 'block'
              modal.appendChild(letterContent)

              // Close button
              let closeBtn = document.createElement('button')
              closeBtn.innerText = '×'
              closeBtn.style.position = 'absolute'
              closeBtn.style.top = '12px'
              closeBtn.style.right = '18px'
              closeBtn.style.fontSize = '2.2rem'
              closeBtn.style.background = 'transparent'
              closeBtn.style.border = 'none'
              closeBtn.style.cursor = 'pointer'
              closeBtn.style.color = '#b08d57'
              closeBtn.style.zIndex = 10
              closeBtn.setAttribute('aria-label', 'Close')
              closeBtn.addEventListener('click', function() {
                let overlay = document.getElementById('letter-modal-overlay')
                if (overlay) {
                  overlay.remove()
                }
              })
              modal.appendChild(closeBtn)

              // Add close on clicking outside modal
              overlay.addEventListener('mousedown', function(e) {
                if (e.target === overlay) {
                  overlay.remove()
                }
              })

              overlay.appendChild(modal)
              document.body.appendChild(overlay)
            })
          }
        )
      }

      lastTimestamp = data[i]['timestamp']
    }

    drawFamilyInteractionLines(eventsGroup, familyInteractionLineGroups)
  })
}

main(STORY_SCRIPT_FILE)
